import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SpecList from './components/SpecList';
import ModelSpecListPanel from './components/ModelSpecListPanel';
import AddSpecPopup from './components/AddSpecPopup';
import DropdownManagePage from './components/DropdownManagePage';
import AsRepairAddPage from './components/AsRepairAddPage';
import AsRepairManagePage from './components/AsRepairManagePage';
import ProductRegistrationSettingsPage from './components/ProductRegistrationSettingsPage';
import AsRepairQtyChartSettingsPage from './components/AsRepairQtyChartSettingsPage';
import { normalizeAsRepairQtyChartConfig } from './utils/asRepairQtyChartConfig';
import { normalizeAsRepairListExportConfig } from './utils/asRepairListExportConfig';
import {
    buildDefaultAsRepairFormConfig,
    normalizeAsRepairFormConfig,
    AS_REPAIR_MANAGEMENT_SCOPE,
} from './utils/asRepairFormConfig';
import {
    alignSpecsWithRegistrationConfig,
    buildDefaultProductRegistrationConfig,
    getProductTypeFromListTypeFilters,
    normalizeProductRegistrationConfig,
} from './utils/productRegistrationConfig';
import KoreaRegionMap from './components/KoreaRegionMap';
import AppleSwitchCheckbox from './components/AppleSwitchCheckbox';
import { getApiBaseUrl, readJsonResponse } from './apiBase';
import {
    createSpecFileListValue,
    detectListResponseStillHasEmbeddedAssets,
    ensureWiringDiagramSpecInSpecs,
    FIRMWARE_SPEC_TITLE,
    isUploadSpecTitle,
    mergeProductSpecsAssets,
    mergeReviewDiffWithAssetSpecs,
    modelHasVisibleUploadAssets,
    modelSpecsNeedAssetLoad,
    parseSpecFileListValue,
    stripListProductRowForFetch,
} from './utils/specFiles';
import './styles.css';

const JSON_HEADERS = {
    'Content-Type': 'application/json'
};

/** 서버 `GET/POST /api/products`의 `listScope` — 메인 리스트(`products`) vs 모델별 카탈로그(`products_model_spec`) */
const PRODUCT_LIST_SCOPE_MAIN = 'main';
const PRODUCT_LIST_SCOPE_MODEL_SPEC = 'modelSpec';
const PRODUCT_LIST_SCOPE_REVIEW = 'review';

const buildAssetCacheKey = (listScope, productId) => `${listScope}:${Number(productId)}`;
const createInitialSpecFilters = () => ([
    { title: '', query: '' },
    { title: '', query: '' },
    { title: '', query: '' }
]);
const EXCLUDED_FILTER_TITLES = new Set(['조절기 이미지', '제어기 이미지', '펌웨어 파일', '결선도/발주서/체크리스트', '결선도 파일', '비고']);

/** 메뉴(워크스페이스) 페이지 ↔ 브라우저 히스토리 동기화 */
const WORKSPACE_PAGE_QUERY = 'page';
const WORKSPACE_PAGE_KEYS = new Set(['list', 'message', 'review', 'dropdowns', 'modelSpecList', 'settings', 'logs', 'productRegisterSettings', 'asRepair', 'asRepairAdd', 'asRepairPersonal', 'asRepairPersonalAdd', 'qtyChartSettings', 'permissions']);

const readWorkspacePageFromLocation = () => {
    if (typeof window === 'undefined') {
        return 'list';
    }
    try {
        const raw = new URLSearchParams(window.location.search).get(WORKSPACE_PAGE_QUERY);
        if (raw && WORKSPACE_PAGE_KEYS.has(raw)) {
            return raw;
        }
    } catch {
        // ignore
    }
    return 'list';
};

const buildWorkspaceUrl = (page) => {
    const url = new URL(window.location.href);
    if (page === 'list') {
        url.searchParams.delete(WORKSPACE_PAGE_QUERY);
    } else {
        url.searchParams.set(WORKSPACE_PAGE_QUERY, page);
    }
    return `${url.pathname}${url.search}${url.hash}`;
};

const currentPathSearchHash = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

const REJECT_MAILBOX_META = '__BNS_JSON__';

/** 두 번째 줄(거부 사유)에 라벨이 없으면 붙여서 표시 */
const formatListRejectReasonLine = (rowReason) => {
    const s = String(rowReason ?? '').trim();
    if (/^\s*승인\s*거부\s*내용\s*:/i.test(s)) {
        return s;
    }
    if (!s || s === '-') {
        return '승인 거부 내용:-';
    }
    return `승인 거부 내용:${s}`;
};

/** 리스트 승인 거부 알림 본문 파싱(표시용 + 재수정용 productId) */
const parseListRejectMailboxContent = (raw) => {
    const full = String(raw || '');
    const idx = full.indexOf(REJECT_MAILBOX_META);
    let visible = idx >= 0 ? full.slice(0, idx).trim() : full.trim();
    let productId = null;
    if (idx >= 0) {
        const tail = full.slice(idx + REJECT_MAILBOX_META.length).trim();
        try {
            const meta = JSON.parse(tail);
            if (meta && meta.productId != null && !Number.isNaN(Number(meta.productId))) {
                productId = Number(meta.productId);
            }
        } catch {
            // ignore
        }
    }
    const lines = visible.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
    if (lines.length >= 2) {
        return {
            productId,
            rowMeta: lines[0],
            rowReason: formatListRejectReasonLine(lines[1]),
        };
    }
    const idMatch = visible.match(/리스트ID\s*:\s*(\d+)/);
    if (idMatch) {
        productId = Number(idMatch[1]);
    }
    const reasonMatch = visible.match(/승인\s*거부\s*내용\s*:\s*(.+)$/i)
        || visible.match(/승인\s*거부내용\s*:\s*(.+)$/i);
    const rowReason = reasonMatch ? String(reasonMatch[1] || '').trim() : '';
    let rowMeta = visible;
    if (reasonMatch) {
        rowMeta = visible.slice(0, reasonMatch.index).replace(/\s*\/\s*$/, '').trim();
    }
    rowMeta = rowMeta.replace(/\s*\/\s*리스트ID\s*:\s*\d+/i, '').replace(/\s*리스트ID\s*:\s*\d+/i, '').trim();
    return {
        productId,
        rowMeta: rowMeta || visible,
        rowReason: formatListRejectReasonLine(rowReason || '-'),
    };
};

const normalizeProductRow = (row, productRegistrationConfig) => {
    if (!row || typeof row !== 'object') {
        return row;
    }
    const config = productRegistrationConfig || buildDefaultProductRegistrationConfig();
    let history = row.reviewRejectHistory ?? row.review_reject_history_json ?? row.review_reject_history;
    if (typeof history === 'string') {
        try {
            const parsed = JSON.parse(history);
            history = Array.isArray(parsed) ? parsed : [];
        } catch {
            history = [];
        }
    } else if (!Array.isArray(history)) {
        history = [];
    }
    const rawStatus = row.reviewStatus ?? row.review_status;
    const rs = rawStatus != null && String(rawStatus).trim() !== ''
        ? String(rawStatus).trim().toLowerCase()
        : 'approved';
    const pendingName = row.pendingRequesterName ?? row.pending_requester_name ?? '';
    const alignedSpecs = alignSpecsWithRegistrationConfig(
        row.type,
        ensureWiringDiagramSpecInSpecs(Array.isArray(row.specs) ? row.specs : []),
        config,
    );
    const specsAssetsDeferred = modelSpecsNeedAssetLoad({
        ...row,
        specs: alignedSpecs,
        specsAssetsDeferred: !!row.specsAssetsDeferred,
    }) || alignedSpecs.some((spec) => isUploadSpecTitle(spec?.title) || String(spec?.inputType || '').trim().toLowerCase() === 'file');
    return {
        ...row,
        reviewStatus: rs,
        reviewRejectHistory: history,
        pendingRequesterName: pendingName != null ? String(pendingName) : '',
        specs: alignedSpecs,
        specsAssetsDeferred,
    };
};

const realignProductSpecsWithConfig = (row, productRegistrationConfig) => {
    if (!row || typeof row !== 'object') {
        return row;
    }
    return {
        ...row,
        specs: alignSpecsWithRegistrationConfig(row.type, row.specs || [], productRegistrationConfig),
    };
};
const COMPANY_LOGO_LIGHT_URL = '/company-logo.png';
const COMPANY_LOGO_DARK_URL = '/company-logo-dark.png';
const SW_PATH = '/sw.js';

const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

function App() {
    const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
    const [isAuthenticated, setIsAuthenticated] = useState(() => localStorage.getItem('bns-auth') === 'true');
    const [currentUser, setCurrentUser] = useState(() => {
        const raw = localStorage.getItem('bns-user');
        if (!raw) {
            return null;
        }

        try {
            return JSON.parse(raw);
        } catch (error) {
            return null;
        }
    });
    const [authMode, setAuthMode] = useState('login');
    const [loginForm, setLoginForm] = useState({ name: '', password: '' });
    const [signupForm, setSignupForm] = useState({ name: '', phoneNumber: '', password: '', confirmPassword: '' });
    const [loginError, setLoginError] = useState('');
    const [signupError, setSignupError] = useState('');
    const [signupSuccess, setSignupSuccess] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isSigningUp, setIsSigningUp] = useState(false);
    const [activeFormType, setActiveFormType] = useState(null);
    /** 제품 등록 폼을 연 페이지(저장 시점의 activePage와 무관하게 동일 테이블로만 저장) */
    const [addFormListScope, setAddFormListScope] = useState(null);
    const [activePage, setActivePage] = useState(() => readWorkspacePageFromLocation());
    const [models, setModels] = useState([]);
    const [loadError, setLoadError] = useState('');
    const [modelSpecListModels, setModelSpecListModels] = useState([]);
    const [modelSpecListError, setModelSpecListError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [specFilters, setSpecFilters] = useState(createInitialSpecFilters);
    const [mapAddressFilter, setMapAddressFilter] = useState(null);
    const [listTypeFilters, setListTypeFilters] = useState({
        adjuster: true,
        controller: true,
    });
    const [permissionUsers, setPermissionUsers] = useState([]);
    const [initialPermissionUsers, setInitialPermissionUsers] = useState([]);
    const [permissionError, setPermissionError] = useState('');
    const [isPermissionLoading, setIsPermissionLoading] = useState(false);
    const [isSavingPermissions, setIsSavingPermissions] = useState(false);
    /** 저장 시 일괄 삭제 — 확인 후 ID만 보관 */
    const [pendingDeleteUserIds, setPendingDeleteUserIds] = useState([]);
    const [pendingReviewProducts, setPendingReviewProducts] = useState([]);
    const [isPendingReviewLoading, setIsPendingReviewLoading] = useState(false);
    const [isProductsListLoading, setIsProductsListLoading] = useState(false);
    const [isModelSpecListLoading, setIsModelSpecListLoading] = useState(false);
    const [loadingAssetModelIds, setLoadingAssetModelIds] = useState({});
    /** 상세 열 때만 로드한 업로드 사양(이미지·파일) — 목록 본문과 분리 */
    const [productAssetSpecsByKey, setProductAssetSpecsByKey] = useState({});
    const prevActivePageRef = useRef(activePage);
    const assetFetchInFlightRef = useRef(new Map());
    const [pendingReviewError, setPendingReviewError] = useState('');
    const [rejectTargetId, setRejectTargetId] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [isRejecting, setIsRejecting] = useState(false);
    const [mailboxItems, setMailboxItems] = useState([]);
    const [mailboxUnreadCount, setMailboxUnreadCount] = useState(0);
    const [isMailboxLoading, setIsMailboxLoading] = useState(false);
    const [mailboxError, setMailboxError] = useState('');
    const [selectedMailboxIds, setSelectedMailboxIds] = useState([]);
    const [isDeletingMailboxItems, setIsDeletingMailboxItems] = useState(false);
    const [retryEditRequest, setRetryEditRequest] = useState(null);
    const [logs, setLogs] = useState([]);
    const [logError, setLogError] = useState('');
    const [isLogLoading, setIsLogLoading] = useState(false);
    const [logFilters, setLogFilters] = useState({ time: '', name: '', target: '', description: '' });
    const [logPage, setLogPage] = useState(1);
    const LOG_PAGE_SIZE = 100;
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('bns-dark-mode') === 'true');
    const [showPasswordChange, setShowPasswordChange] = useState(false);
    const [passwordChangeForm, setPasswordChangeForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [passwordChangeStatus, setPasswordChangeStatus] = useState('');
    const [appToastMessage, setAppToastMessage] = useState('');
    const [appToastVisible, setAppToastVisible] = useState(false);
    const [appToastType, setAppToastType] = useState('success');
    const appToastTimerRef = useRef(null);
    const [specDropdownOptions, setSpecDropdownOptions] = useState([]);
    const [productRegistrationConfig, setProductRegistrationConfig] = useState(() => buildDefaultProductRegistrationConfig());
    const [asRepairFormConfig, setAsRepairFormConfig] = useState(() => buildDefaultAsRepairFormConfig());
    const [asRepairPersonalFormConfig, setAsRepairPersonalFormConfig] = useState(() => buildDefaultAsRepairFormConfig());
    const [asRepairQtyChartConfig, setAsRepairQtyChartConfig] = useState(() => normalizeAsRepairQtyChartConfig(null));
    const [asRepairListExportConfig, setAsRepairListExportConfig] = useState(() => normalizeAsRepairListExportConfig(null));
    const [pushRegistering, setPushRegistering] = useState(false);
    const [expandFromMapRequest, setExpandFromMapRequest] = useState(null);
    const [bulkFirmwareMode, setBulkFirmwareMode] = useState(false);
    const [bulkFirmwareFile, setBulkFirmwareFile] = useState(null);
    const [bulkFirmwareMemo, setBulkFirmwareMemo] = useState('');
    const [bulkFirmwareTargetIds, setBulkFirmwareTargetIds] = useState([]);
    const [isBulkFirmwareSubmitting, setIsBulkFirmwareSubmitting] = useState(false);
    const [showBulkFirmwarePendingPopup, setShowBulkFirmwarePendingPopup] = useState(false);
    const [bulkPendingSelectedIds, setBulkPendingSelectedIds] = useState([]);
    const [isBulkPendingApproving, setIsBulkPendingApproving] = useState(false);
    const [bulkPendingRejectReason, setBulkPendingRejectReason] = useState('');
    const [isBulkPendingRejecting, setIsBulkPendingRejecting] = useState(false);

    const ensurePushSubscription = async (userName) => {
        if (pushRegistering) {
            return false;
        }
        setPushRegistering(true);
        if (!userName || !('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
            showAppToast('현재 브라우저는 웹푸시를 지원하지 않습니다.', 'error');
            setPushRegistering(false);
            return false;
        }
        if (!window.isSecureContext) {
            showAppToast('웹푸시는 HTTPS(또는 localhost)에서만 동작합니다.', 'error');
            setPushRegistering(false);
            return false;
        }

        let registration;
        try {
            registration = await navigator.serviceWorker.register(SW_PATH);
        } catch (error) {
            showAppToast('서비스워커 등록에 실패했습니다.', 'error');
            setPushRegistering(false);
            return false;
        }

        let permission = Notification.permission;
        if (permission === 'default') {
            const wantsPush = window.confirm('알림을 받으시겠습니까?');
            if (!wantsPush) {
                return;
            }
            permission = await Notification.requestPermission();
        }
        if (permission !== 'granted') {
            showAppToast('알림 권한이 허용되지 않아 웹푸시를 받을 수 없습니다.', 'error');
            setPushRegistering(false);
            return false;
        }

        try {
            const keyResponse = await fetch(`${apiBaseUrl}/push/vapid-public-key`);
            const keyData = await readJsonResponse(keyResponse);
            const applicationServerKey = urlBase64ToUint8Array(String(keyData?.publicKey || '').trim());
            if (!applicationServerKey.length) {
                return;
            }

            let subscription = await registration.pushManager.getSubscription();
            if (!subscription) {
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey,
                });
            }

            const subscribeResponse = await fetch(`${apiBaseUrl}/push/subscribe`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    requesterName: userName,
                    subscription,
                }),
            });
            await readJsonResponse(subscribeResponse);
            showAppToast('웹푸시가 활성화되었습니다.', 'success');
            setPushRegistering(false);
            return true;
        } catch (error) {
            showAppToast(error?.message || '웹푸시 등록에 실패했습니다.', 'error');
            setPushRegistering(false);
            return false;
        }
    };

    const toggleDarkMode = (checked) => {
        setDarkMode(checked);
        localStorage.setItem('bns-dark-mode', String(checked));
        if (checked) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    };

    useEffect(() => {
        if (darkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [darkMode]);

    const canReviewList = !!currentUser?.permissions?.canReviewList;
    const canEditProduct = !!currentUser?.permissions?.canEditProduct;
    const canRegisterProduct = !!currentUser?.permissions?.canRegisterProduct;
    const canViewDetail = !!currentUser?.permissions?.canViewDetail;
    const canDownloadFirmware = !!currentUser?.permissions?.canDownloadFirmware;
    const canDownloadWiringDiagram = !!currentUser?.permissions?.canDownloadWiringDiagram;
    const canManagePermissions = !!currentUser?.permissions?.canManagePermissions;
    const canViewLogs = !!currentUser?.permissions?.canViewLogs || canManagePermissions;
    const canManageSpecDropdowns = canEditProduct || canRegisterProduct || canManagePermissions;
    const perms = currentUser?.permissions || {};
    const canManageModelSpecList = Object.prototype.hasOwnProperty.call(perms, 'canManageModelSpecList')
        ? !!perms.canManageModelSpecList
        : !!perms.canManagePermissions;
    const canManageAsRepair = !!perms.canManageAsRepair;

    const resolveWorkspacePage = useCallback((page) => {
        const p = String(page || 'list');
        if (!WORKSPACE_PAGE_KEYS.has(p)) {
            return 'list';
        }
        if (p === 'review' && !canReviewList) {
            return 'list';
        }
        if (p === 'logs' && !canViewLogs) {
            return 'list';
        }
        if (p === 'permissions' && !canManagePermissions) {
            return 'list';
        }
        if (p === 'dropdowns' && !canManageSpecDropdowns) {
            return 'list';
        }
        if (p === 'modelSpecList' && !canManageModelSpecList) {
            return 'list';
        }
        if (p === 'productRegisterSettings' && !canManagePermissions) {
            return 'list';
        }
        if ((p === 'asRepair' || p === 'asRepairAdd' || p === 'asRepairPersonal' || p === 'asRepairPersonalAdd' || p === 'qtyChartSettings') && !canManageAsRepair) {
            return 'list';
        }
        return p;
    }, [canReviewList, canViewLogs, canManagePermissions, canManageSpecDropdowns, canManageModelSpecList, canManageAsRepair]);

    const goToPage = useCallback((page, { replace = false } = {}) => {
        const next = resolveWorkspacePage(page);
        const target = buildWorkspaceUrl(next);
        const current = currentPathSearchHash();
        if (target === current) {
            setActivePage(next);
            return;
        }
        if (replace) {
            window.history.replaceState({ bnsPage: next }, '', target);
        } else {
            window.history.pushState({ bnsPage: next }, '', target);
        }
        setActivePage(next);
    }, [resolveWorkspacePage]);

    useEffect(() => {
        if (!isAuthenticated) {
            return undefined;
        }
        const syncFromHistory = () => {
            const urlPage = readWorkspacePageFromLocation();
            const next = resolveWorkspacePage(urlPage);
            setActivePage(next);
            const target = buildWorkspaceUrl(next);
            if (currentPathSearchHash() !== target) {
                window.history.replaceState({ bnsPage: next }, '', target);
            }
        };
        window.addEventListener('popstate', syncFromHistory);
        return () => window.removeEventListener('popstate', syncFromHistory);
    }, [isAuthenticated, resolveWorkspacePage]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        setActivePage((prev) => {
            const next = resolveWorkspacePage(prev);
            if (next !== prev) {
                window.history.replaceState({ bnsPage: next }, '', buildWorkspaceUrl(next));
            }
            return next;
        });
    }, [isAuthenticated, resolveWorkspacePage]);

    const hideAppToast = useCallback(() => {
        if (appToastTimerRef.current) {
            window.clearTimeout(appToastTimerRef.current);
            appToastTimerRef.current = null;
        }
        setAppToastVisible(false);
        window.setTimeout(() => setAppToastMessage(''), 250);
    }, []);

    const showAppToast = useCallback((message, type = 'success', options = {}) => {
        const { persistent = false, duration = 1600 } = options;
        if (appToastTimerRef.current) {
            window.clearTimeout(appToastTimerRef.current);
            appToastTimerRef.current = null;
        }
        setAppToastType(type);
        setAppToastMessage(message);
        setAppToastVisible(true);
        if (!persistent) {
            appToastTimerRef.current = window.setTimeout(() => {
                hideAppToast();
            }, duration);
        }
    }, [hideAppToast]);

    const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('파일 읽기 실패'));
        reader.readAsDataURL(file);
    });

    const appendFirmwareFileToSpecs = (specs = [], filePayload) => {
        const next = Array.isArray(specs) ? [...specs] : [];
        const targetIndex = next.findIndex((spec) => String(spec?.title || '').trim() === FIRMWARE_SPEC_TITLE);
        if (targetIndex === -1) {
            next.push({
                title: FIRMWARE_SPEC_TITLE,
                fullWidth: true,
                details: createSpecFileListValue({ files: [filePayload] }),
            });
            return next;
        }
        const existingFiles = parseSpecFileListValue(next[targetIndex]?.details);
        next[targetIndex] = {
            ...next[targetIndex],
            // 기존 파일 목록 뒤에 추가(교체 아님)
            details: createSpecFileListValue({ files: [...existingFiles, { ...filePayload }] }),
        };
        return next;
    };

    const approveProductRequest = async (productId) => {
        const response = await fetch(`${apiBaseUrl}/review/products/${productId}/approve`, {
            method: 'PUT',
            headers: JSON_HEADERS,
            body: JSON.stringify({ requesterName: currentUser.name }),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData?.message || '승인 실패');
        }
    };

    const rejectProductRequest = async (productId, reason) => {
        const response = await fetch(`${apiBaseUrl}/review/products/${productId}/reject`, {
            method: 'PUT',
            headers: JSON_HEADERS,
            body: JSON.stringify({
                requesterName: currentUser.name,
                reason: String(reason || '').trim(),
            }),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData?.message || '승인 거부 실패');
        }
    };


    const fetchNormalizedProductRows = async (listScope) => {
        const requesterName = String(currentUser?.name || '').trim();
        const qs = new URLSearchParams();
        if (requesterName) {
            qs.set('requesterName', requesterName);
        }
        qs.set(
            'listScope',
            listScope === PRODUCT_LIST_SCOPE_MODEL_SPEC ? PRODUCT_LIST_SCOPE_MODEL_SPEC : PRODUCT_LIST_SCOPE_MAIN,
        );
        qs.set('deferAssets', '1');
        const query = qs.toString();
        const productsUrl = `${apiBaseUrl}/products?${query}`;
        const response = await fetch(productsUrl, { cache: 'no-store' });
        const rawText = await response.text();
        const trimmed = rawText.trim();
        if (trimmed.startsWith('<')) {
            throw new Error(
                'API 대신 웹페이지(HTML)가 반환되었습니다. 백엔드(server)를 실행한 뒤 `npm start` 개발 서버를 재시작해주세요.'
            );
        }
        let data;
        try {
            data = JSON.parse(trimmed);
        } catch (parseErr) {
            throw new Error(`JSON 파싱 실패: ${trimmed.slice(0, 80)}…`);
        }
        if (!response.ok) {
            const msg = (data && (data.message || data.error))
                || `목록 조회 실패 (HTTP ${response.status})`;
            throw new Error(msg);
        }
        const list = Array.isArray(data) ? data : [];
        const strippedList = list.map(stripListProductRowForFetch);
        const listAssetsDeferred = String(response.headers.get('X-Bns-List-Assets-Deferred') || '').trim() === '1';
        if (detectListResponseStillHasEmbeddedAssets(strippedList)) {
            throw new Error(
                '목록 API 서버가 구버전입니다. server/index.js 와 server/utils/specAssetStrip.js 를 서버에 업로드한 뒤 pm2 restart 하세요. (지금은 목록에 이미지·파일이 포함된 채 내려옵니다)',
            );
        }
        if (!listAssetsDeferred && strippedList.some((row) => (row.specs || []).some((spec) => String(spec?.details || '').includes('__bnsFile')))) {
            throw new Error(
                '목록 API 서버가 구버전입니다. server/index.js 와 server/utils/specAssetStrip.js 업로드 후 pm2 restart 가 필요합니다.',
            );
        }
        return strippedList.map((row) => normalizeProductRow(row, productRegistrationConfig));
    };

    const enrichModelWithLoadedAssets = useCallback((model, listScope = PRODUCT_LIST_SCOPE_MAIN, assetSpecsOverride) => {
        if (!model) {
            return model;
        }
        const cacheKey = buildAssetCacheKey(listScope, model.id);
        const assetSpecs = assetSpecsOverride ?? productAssetSpecsByKey[cacheKey];
        if (!assetSpecs) {
            return {
                ...model,
                specsAssetsDeferred: modelSpecsNeedAssetLoad(model),
            };
        }
        const mergedSpecs = mergeProductSpecsAssets(model.specs, assetSpecs);
        const enriched = {
            ...model,
            specs: mergedSpecs,
            reviewDiffInitial: mergeReviewDiffWithAssetSpecs(model.reviewDiffInitial, assetSpecs),
            reviewDiffResubmit: mergeReviewDiffWithAssetSpecs(model.reviewDiffResubmit, assetSpecs),
            reviewDiff: mergeReviewDiffWithAssetSpecs(model.reviewDiff, assetSpecs),
        };
        return {
            ...enriched,
            specsAssetsDeferred: modelSpecsNeedAssetLoad(enriched),
        };
    }, [productAssetSpecsByKey]);

    const clearAssetCacheForScope = useCallback((listScope) => {
        setProductAssetSpecsByKey((prev) => {
            const prefix = `${listScope}:`;
            const next = { ...prev };
            Object.keys(next).forEach((key) => {
                if (key.startsWith(prefix)) {
                    delete next[key];
                }
            });
            return next;
        });
    }, []);

    const clearLoadingAssetIdsForScope = useCallback((listScope) => {
        setLoadingAssetModelIds((prev) => {
            const prefix = `${listScope}:`;
            const next = { ...prev };
            Object.keys(next).forEach((key) => {
                if (key.startsWith(prefix)) {
                    delete next[key];
                }
            });
            return next;
        });
    }, []);

    const resetAssetStateForScope = useCallback((listScope) => {
        clearAssetCacheForScope(listScope);
        clearLoadingAssetIdsForScope(listScope);
    }, [clearAssetCacheForScope, clearLoadingAssetIdsForScope]);

    const loadProducts = async () => {
        try {
            setIsProductsListLoading(true);
            setLoadError('');
            resetAssetStateForScope(PRODUCT_LIST_SCOPE_MAIN);
            setModels(await fetchNormalizedProductRows(PRODUCT_LIST_SCOPE_MAIN));
        } catch (error) {
            const hint = process.env.NODE_ENV === 'development'
                ? ' 백엔드: `node server/index.js`(기본 4000), 프록시: `src/setupProxy.js`·REACT_APP_DEV_API_TARGET 확인.'
                : '';
            setLoadError(`${error?.message || 'DB 연결 또는 목록 조회에 실패했습니다.'}${hint}`);
        } finally {
            setIsProductsListLoading(false);
        }
    };

    const loadModelSpecListProducts = async () => {
        try {
            setIsModelSpecListLoading(true);
            setModelSpecListError('');
            resetAssetStateForScope(PRODUCT_LIST_SCOPE_MODEL_SPEC);
            setModelSpecListModels(await fetchNormalizedProductRows(PRODUCT_LIST_SCOPE_MODEL_SPEC));
        } catch (error) {
            const hint = process.env.NODE_ENV === 'development'
                ? ' 백엔드: `node server/index.js`(기본 4000), 프록시: `src/setupProxy.js`·REACT_APP_DEV_API_TARGET 확인.'
                : '';
            setModelSpecListError(`${error?.message || 'DB 연결 또는 목록 조회에 실패했습니다.'}${hint}`);
        } finally {
            setIsModelSpecListLoading(false);
        }
    };

    const loadSpecDropdownOptions = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/spec-dropdown-options`);
            const data = await readJsonResponse(response);
            setSpecDropdownOptions(Array.isArray(data) ? data : []);
        } catch (error) {
            setSpecDropdownOptions([]);
        }
    };

    const loadProductRegistrationConfig = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/product-registration-config`, { cache: 'no-store' });
            const data = await readJsonResponse(response);
            setProductRegistrationConfig(normalizeProductRegistrationConfig(data));
            setAsRepairFormConfig(normalizeAsRepairFormConfig(data?.asRepair));
            setAsRepairPersonalFormConfig(normalizeAsRepairFormConfig(data?.asRepairPersonal));
            setAsRepairQtyChartConfig(normalizeAsRepairQtyChartConfig(data?.asRepairQtyChart));
            setAsRepairListExportConfig(normalizeAsRepairListExportConfig(data?.asRepairListExport));
        } catch (error) {
            setProductRegistrationConfig(buildDefaultProductRegistrationConfig());
            setAsRepairFormConfig(buildDefaultAsRepairFormConfig());
            setAsRepairPersonalFormConfig(buildDefaultAsRepairFormConfig());
            setAsRepairQtyChartConfig(normalizeAsRepairQtyChartConfig(null));
            setAsRepairListExportConfig(normalizeAsRepairListExportConfig(null));
        }
    };

    const handleRegistrationSettingsSaved = (productConfig, asRepairConfig, asRepairPersonalConfig) => {
        setProductRegistrationConfig(normalizeProductRegistrationConfig(productConfig));
        setAsRepairFormConfig(normalizeAsRepairFormConfig(asRepairConfig));
        setAsRepairPersonalFormConfig(normalizeAsRepairFormConfig(asRepairPersonalConfig));
    };

    const handleAddSpecDropdownOption = async (specTitle, optionValue) => {
        const trimmed = String(optionValue || '').trim();
        if (!trimmed) {
            const msg = '옵션 값을 입력해주세요.';
            showAppToast(msg, 'error');
            throw new Error(msg);
        }
        if (!currentUser?.name) {
            const msg = '로그인이 필요합니다.';
            showAppToast(msg, 'error');
            throw new Error(msg);
        }
        try {
            const response = await fetch(`${apiBaseUrl}/spec-dropdown-options`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    requesterName: currentUser.name,
                    specTitle,
                    optionValue: trimmed,
                }),
            });
            if (response.status === 409) {
                await loadSpecDropdownOptions();
                showAppToast('이미 등록된 옵션입니다. 목록에서 선택합니다.', 'success');
                return { optionValue: trimmed };
            }
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.message || '드롭다운 옵션 추가에 실패했습니다.');
            }
            await loadSpecDropdownOptions();
            showAppToast('드롭다운 옵션이 추가되었습니다.', 'success');
            return { optionValue: trimmed };
        } catch (error) {
            if (error?.message) {
                showAppToast(error.message, 'error');
            }
            throw error;
        }
    };

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        Promise.all([loadSpecDropdownOptions(), loadProductRegistrationConfig()]);
    }, [isAuthenticated, currentUser?.name]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        const realign = (row) => realignProductSpecsWithConfig(row, productRegistrationConfig);
        setModels((prev) => prev.map(realign));
        setModelSpecListModels((prev) => prev.map(realign));
        setPendingReviewProducts((prev) => prev.map(realign));
    }, [productRegistrationConfig, isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        if (activePage === 'list') {
            loadProducts();
        }
    }, [isAuthenticated, activePage]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        if (activePage === 'modelSpecList') {
            loadModelSpecListProducts();
        }
    }, [isAuthenticated, activePage]);

    /** 다른 페이지로 나가면 메모리에 쌓인 파일·이미지 캐시 제거 */
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        const prevPage = prevActivePageRef.current;
        if (prevPage === 'list' && activePage !== 'list') {
            resetAssetStateForScope(PRODUCT_LIST_SCOPE_MAIN);
        }
        if (prevPage === 'modelSpecList' && activePage !== 'modelSpecList') {
            resetAssetStateForScope(PRODUCT_LIST_SCOPE_MODEL_SPEC);
        }
        if (prevPage === 'review' && activePage !== 'review') {
            resetAssetStateForScope(PRODUCT_LIST_SCOPE_REVIEW);
        }
        prevActivePageRef.current = activePage;
    }, [activePage, isAuthenticated, resetAssetStateForScope]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setSignupSuccess('');
        setIsLoggingIn(true);

        try {
            const response = await fetch(`${apiBaseUrl}/login`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify(loginForm)
            });

            const data = await readJsonResponse(response);
            const loggedInUser = data?.user || null;

            setIsAuthenticated(true);
            localStorage.setItem('bns-auth', 'true');
            setCurrentUser(loggedInUser);
            localStorage.setItem('bns-user', JSON.stringify(loggedInUser));
            setLoginForm({ name: '', password: '' });
            ensurePushSubscription(String(loggedInUser?.name || '').trim());
        } catch (error) {
            setLoginError(error?.message || '아이디 또는 비밀번호를 확인해주세요.');
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setSignupError('');
        setSignupSuccess('');

        if (!signupForm.name.trim() || !signupForm.phoneNumber.trim() || !signupForm.password.trim()) {
            setSignupError('이름, 전화번호, 비밀번호를 입력해주세요.');
            return;
        }

        if (signupForm.password !== signupForm.confirmPassword) {
            setSignupError('비밀번호 확인이 일치하지 않습니다.');
            return;
        }

        setIsSigningUp(true);
        try {
            const response = await fetch(`${apiBaseUrl}/signup`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    name: signupForm.name,
                    phoneNumber: signupForm.phoneNumber,
                    password: signupForm.password
                })
            });

            if (!response.ok) {
                throw new Error('회원가입 실패');
            }

            setSignupForm({ name: '', phoneNumber: '', password: '', confirmPassword: '' });
            setSignupSuccess('회원가입이 완료되었습니다. 로그인 해주세요.');
            setAuthMode('login');
        } catch (error) {
            setSignupError('이미 사용 중인 아이디이거나 가입에 실패했습니다.');
        } finally {
            setIsSigningUp(false);
        }
    };

    const handleLogout = async () => {
        if (currentUser?.name) {
            try {
                await fetch(`${apiBaseUrl}/logs/event`, {
                    method: 'POST',
                    headers: JSON_HEADERS,
                    body: JSON.stringify({
                        requesterName: currentUser.name,
                        actionType: 'logout',
                        targetType: 'auth',
                        description: '로그아웃',
                    })
                });
            } catch (error) {
                // Ignore logging failure on logout.
            }
        }

        setIsAuthenticated(false);
        setCurrentUser(null);
        setActiveFormType(null);
        setAddFormListScope(null);
        setActivePage('list');
        window.history.replaceState(null, '', buildWorkspaceUrl('list'));
        setSearchQuery('');
        setSpecFilters(createInitialSpecFilters());
        setListTypeFilters({ adjuster: true, controller: true });
        setSpecDropdownOptions([]);
        setProductRegistrationConfig(buildDefaultProductRegistrationConfig());
        setModels([]);
        setModelSpecListModels([]);
        setProductAssetSpecsByKey({});
        setLoadingAssetModelIds({});
        setModelSpecListError('');
        setLoadError('');
        setShowPasswordChange(false);
        setPasswordChangeForm({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        });
        setPasswordChangeStatus('');
        localStorage.removeItem('bns-auth');
        localStorage.removeItem('bns-user');
    };

    const openAddPage = (type) => {
        if (!canEditProduct) {
            setLoadError('제품 등록 권한이 없습니다. 관리자에게 문의해주세요.');
            return;
        }

        setActiveFormType(type);
        setAddFormListScope((prev) => (
            prev != null
                ? prev
                : (activePage === 'modelSpecList' ? PRODUCT_LIST_SCOPE_MODEL_SPEC : PRODUCT_LIST_SCOPE_MAIN)
        ));
    };

    const openAddPageFromListFilters = () => {
        openAddPage(getProductTypeFromListTypeFilters(listTypeFilters));
    };

    const closeAddPage = () => {
        setActiveFormType(null);
        setAddFormListScope(null);
    };

    const handleAddModel = async (model, type) => {
        try {
            const scopeForSave = addFormListScope === PRODUCT_LIST_SCOPE_MODEL_SPEC
                ? PRODUCT_LIST_SCOPE_MODEL_SPEC
                : PRODUCT_LIST_SCOPE_MAIN;
            const response = await fetch(`${apiBaseUrl}/products`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    siteName: model.siteName,
                    productName: model.name,
                    householdCount: model.householdCount || '',
                    type,
                    specs: model.specs,
                    sido: model.sido || '',
                    gugun: model.gugun || '',
                    dong: model.dong || '',
                    detailAddress: model.detailAddress || '',
                    actorName: currentUser?.name || null,
                    listScope: scopeForSave,
                })
            });

            const rawText = await response.text();
            const trimmed = rawText.trim();
            if (trimmed.startsWith('<')) {
                throw new Error(
                    'API 대신 웹페이지(HTML)가 반환되었습니다. 백엔드(server)를 실행한 뒤 개발 서버를 재시작해주세요.'
                );
            }
            let postBody;
            try {
                postBody = trimmed ? JSON.parse(rawText) : {};
            } catch (parseErr) {
                throw new Error('서버 응답을 JSON으로 읽을 수 없습니다.');
            }
            if (!response.ok) {
                throw new Error(postBody.message || postBody.error || '저장에 실패했습니다.');
            }

            closeAddPage();
            try {
                await (scopeForSave === PRODUCT_LIST_SCOPE_MODEL_SPEC ? loadModelSpecListProducts() : loadProducts());
            } catch (loadErr) {
                showAppToast('저장은 완료되었으나 목록 새로고침에 실패했습니다. 목록에서 새로고침 해주세요.', 'error');
            }
            return true;
        } catch (error) {
            const msg = error?.message || '저장에 실패했습니다.';
            showAppToast(msg, 'error');
            return false;
        }
    };

    const loadPermissionUsers = async () => {
        if (!canManagePermissions || !currentUser?.name) {
            return;
        }

        setPermissionError('');
        setIsPermissionLoading(true);

        try {
            const response = await fetch(`${apiBaseUrl}/users?requesterName=${encodeURIComponent(currentUser.name)}`);
            if (!response.ok) {
                throw new Error('권한 사용자 목록 조회 실패');
            }

            const data = await response.json();
            const users = Array.isArray(data) ? data : [];
            setPermissionUsers(users);
            setInitialPermissionUsers(users);
        } catch (error) {
            setPermissionError('권한 사용자 목록을 불러오지 못했습니다.');
        } finally {
            setIsPermissionLoading(false);
        }
    };

    const loadPendingReviewProducts = async () => {
        if (!canReviewList || !currentUser?.name) {
            return;
        }

        setPendingReviewError('');
        setIsPendingReviewLoading(true);
        try {
            resetAssetStateForScope(PRODUCT_LIST_SCOPE_REVIEW);
            const response = await fetch(`${apiBaseUrl}/review/pending-products?requesterName=${encodeURIComponent(currentUser.name)}`, { cache: 'no-store' });
            const data = await readJsonResponse(response);
            const list = Array.isArray(data) ? data : [];
            setPendingReviewProducts(list.map((row) => normalizeProductRow(row, productRegistrationConfig)));
        } catch (error) {
            setPendingReviewError('승인 대기 목록을 불러오지 못했습니다.');
        } finally {
            setIsPendingReviewLoading(false);
        }
    };

    const loadProductAssets = useCallback(async (productId, listScope = PRODUCT_LIST_SCOPE_MAIN) => {
        const normalizedId = Number(productId);
        if (!currentUser?.name || !normalizedId || Number.isNaN(normalizedId)) {
            return null;
        }

        const cacheKey = buildAssetCacheKey(listScope, normalizedId);
        const inFlight = assetFetchInFlightRef.current.get(cacheKey);
        if (inFlight) {
            return inFlight;
        }

        const isModelSpec = listScope === PRODUCT_LIST_SCOPE_MODEL_SPEC;
        const currentList = isModelSpec ? modelSpecListModels : models;
        const existing = currentList.find((item) => Number(item.id) === normalizedId);
        const setErr = isModelSpec ? setModelSpecListError : setLoadError;

        if (Object.prototype.hasOwnProperty.call(productAssetSpecsByKey, cacheKey)) {
            return enrichModelWithLoadedAssets(existing, listScope, productAssetSpecsByKey[cacheKey]);
        }

        const fetchPromise = (async () => {
            setLoadingAssetModelIds((prev) => ({ ...prev, [cacheKey]: true }));
            try {
                const qs = new URLSearchParams({
                    requesterName: currentUser.name,
                    listScope: isModelSpec ? PRODUCT_LIST_SCOPE_MODEL_SPEC : PRODUCT_LIST_SCOPE_MAIN,
                });
                const response = await fetch(
                    `${apiBaseUrl}/products/${normalizedId}/assets?${qs.toString()}`,
                    { cache: 'no-store' },
                );
                const data = await readJsonResponse(response);
                const assetSpecs = Array.isArray(data?.specs) ? data.specs : [];
                if (assetSpecs.length === 0) {
                    setErr('이미지·파일 정보를 불러오지 못했습니다.');
                } else {
                    setErr('');
                }
                setProductAssetSpecsByKey((prev) => ({ ...prev, [cacheKey]: assetSpecs }));
                return enrichModelWithLoadedAssets(existing, listScope, assetSpecs);
            } catch (error) {
                setErr(error?.message || '파일 불러오기에 실패했습니다.');
                setProductAssetSpecsByKey((prev) => (
                    Object.prototype.hasOwnProperty.call(prev, cacheKey)
                        ? prev
                        : { ...prev, [cacheKey]: [] }
                ));
                return null;
            } finally {
                setLoadingAssetModelIds((prev) => {
                    const next = { ...prev };
                    delete next[cacheKey];
                    return next;
                });
                assetFetchInFlightRef.current.delete(cacheKey);
            }
        })();

        assetFetchInFlightRef.current.set(cacheKey, fetchPromise);
        return fetchPromise;
    }, [
        apiBaseUrl,
        currentUser?.name,
        enrichModelWithLoadedAssets,
        models,
        modelSpecListModels,
        productAssetSpecsByKey,
    ]);

    const loadReviewProductAssets = useCallback(async (productId) => {
        const normalizedId = Number(productId);
        if (!canReviewList || !currentUser?.name || !normalizedId || Number.isNaN(normalizedId)) {
            return null;
        }

        const listScope = PRODUCT_LIST_SCOPE_REVIEW;
        const cacheKey = buildAssetCacheKey(listScope, normalizedId);
        const inFlight = assetFetchInFlightRef.current.get(cacheKey);
        if (inFlight) {
            return inFlight;
        }

        const existing = pendingReviewProducts.find((item) => Number(item.id) === normalizedId);
        if (Object.prototype.hasOwnProperty.call(productAssetSpecsByKey, cacheKey)) {
            return enrichModelWithLoadedAssets(existing, listScope, productAssetSpecsByKey[cacheKey]);
        }

        const fetchPromise = (async () => {
            setLoadingAssetModelIds((prev) => ({ ...prev, [cacheKey]: true }));
            try {
                const response = await fetch(
                    `${apiBaseUrl}/review/products/${normalizedId}/assets?requesterName=${encodeURIComponent(currentUser.name)}`,
                    { cache: 'no-store' },
                );
                const data = await readJsonResponse(response);
                const assetSpecs = Array.isArray(data?.specs) ? data.specs : [];
                setProductAssetSpecsByKey((prev) => ({ ...prev, [cacheKey]: assetSpecs }));
                return enrichModelWithLoadedAssets(existing, listScope, assetSpecs);
            } catch (error) {
                setPendingReviewError(error?.message || '파일 불러오기에 실패했습니다.');
                setProductAssetSpecsByKey((prev) => (
                    Object.prototype.hasOwnProperty.call(prev, cacheKey)
                        ? prev
                        : { ...prev, [cacheKey]: [] }
                ));
                return null;
            } finally {
                setLoadingAssetModelIds((prev) => {
                    const next = { ...prev };
                    delete next[cacheKey];
                    return next;
                });
                assetFetchInFlightRef.current.delete(cacheKey);
            }
        })();

        assetFetchInFlightRef.current.set(cacheKey, fetchPromise);
        return fetchPromise;
    }, [
        apiBaseUrl,
        canReviewList,
        currentUser?.name,
        enrichModelWithLoadedAssets,
        pendingReviewProducts,
        productAssetSpecsByKey,
    ]);

    const handleLoadMainListAssets = useCallback(
        (productId) => loadProductAssets(productId, PRODUCT_LIST_SCOPE_MAIN),
        [loadProductAssets],
    );

    const handleLoadModelSpecListAssets = useCallback(
        (productId) => loadProductAssets(productId, PRODUCT_LIST_SCOPE_MODEL_SPEC),
        [loadProductAssets],
    );

    const loadMailboxUnreadCount = async () => {
        if (!currentUser?.name) {
            return;
        }
        try {
            const response = await fetch(`${apiBaseUrl}/mailbox/unread-count?requesterName=${encodeURIComponent(currentUser.name)}`);
            const data = await readJsonResponse(response);
            setMailboxUnreadCount(Number(data?.unreadCount || 0));
        } catch (error) {
            // Ignore badge load failure to avoid blocking UI.
        }
    };

    const loadMailboxItems = async () => {
        if (!currentUser?.name) {
            return;
        }
        setMailboxError('');
        setIsMailboxLoading(true);
        try {
            const response = await fetch(`${apiBaseUrl}/mailbox?requesterName=${encodeURIComponent(currentUser.name)}`);
            const data = await readJsonResponse(response);
            setMailboxItems(Array.isArray(data) ? data : []);
            setSelectedMailboxIds([]);
        } catch (error) {
            setMailboxError('우편함을 불러오지 못했습니다.');
        } finally {
            setIsMailboxLoading(false);
        }
    };

    const markAllMailboxAsRead = async () => {
        if (!currentUser?.name) {
            return;
        }
        try {
            await fetch(`${apiBaseUrl}/mailbox/read-all`, {
                method: 'PUT',
                headers: JSON_HEADERS,
                body: JSON.stringify({ requesterName: currentUser.name }),
            });
            setMailboxUnreadCount(0);
            setMailboxItems((prev) => prev.map((item) => ({ ...item, isRead: true })));
        } catch (error) {
            // ignore
        }
    };

    const toggleMailboxItemSelection = (itemId, checked) => {
        setSelectedMailboxIds((prev) => {
            if (checked) {
                return prev.includes(itemId) ? prev : [...prev, itemId];
            }
            return prev.filter((id) => id !== itemId);
        });
    };
    const handleToggleSelectAllMailbox = () => {
        const selectableIds = mailboxItems
            .filter((item) => item.title !== '리스트 승인 거부')
            .map((item) => item.id);
        if (selectableIds.length === 0) {
            return;
        }
        const isAllSelected = selectableIds.every((id) => selectedMailboxIds.includes(id));
        setSelectedMailboxIds(isAllSelected ? [] : selectableIds);
    };
    const selectableMailboxIds = mailboxItems
        .filter((item) => item.title !== '리스트 승인 거부')
        .map((item) => item.id);
    const isAllMailboxSelected = selectableMailboxIds.length > 0
        && selectableMailboxIds.every((id) => selectedMailboxIds.includes(id));

    const handleDeleteSelectedMailboxItems = async () => {
        if (!currentUser?.name || selectedMailboxIds.length === 0) {
            window.alert('삭제할 알림을 선택해주세요.');
            return;
        }
        const ok = window.confirm(`선택한 알림 ${selectedMailboxIds.length}개를 삭제하시겠습니까?`);
        if (!ok) {
            return;
        }
        setIsDeletingMailboxItems(true);
        setMailboxError('');
        try {
            const response = await fetch(`${apiBaseUrl}/mailbox/delete-selected`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    requesterName: currentUser.name,
                    itemIds: selectedMailboxIds,
                }),
            });
            if (!response.ok) {
                throw new Error('알림 삭제 실패');
            }
            await Promise.all([loadMailboxItems(), loadMailboxUnreadCount()]);
        } catch (error) {
            setMailboxError('선택한 알림 삭제에 실패했습니다.');
        } finally {
            setIsDeletingMailboxItems(false);
        }
    };

    const handleMailboxRetryEdit = (item) => {
        if (!canEditProduct) {
            window.alert('리스트 수정 권한이 없습니다. 관리자에게 문의해주세요.');
            return;
        }

        const content = String(item?.content || '');
        const parsed = parseListRejectMailboxContent(content);
        const metaLine = String(parsed.rowMeta || '');
        const commaMeta = metaLine.match(
            /현장명\s*:\s*(.+?)\s*,\s*모델명\s*:\s*(.+?)\s*,\s*타입\s*:\s*(조절기|제어기)/,
        );
        const slashMeta = !commaMeta
            ? metaLine.match(/현장명\s*:\s*(.+?)\s*\/\s*모델명\s*:\s*(.+?)\s*\/\s*타입\s*:\s*(조절기|제어기)/)
            : null;
        const m = commaMeta || slashMeta;
        const type = m?.[3] === '제어기' ? 'controller' : m?.[3] === '조절기' ? 'adjuster' : '';
        goToPage('list', { replace: true });
        if (type === 'adjuster') {
            setListTypeFilters({ adjuster: true, controller: false });
        } else if (type === 'controller') {
            setListTypeFilters({ adjuster: false, controller: true });
        } else {
            setListTypeFilters({ adjuster: true, controller: true });
        }
        setRetryEditRequest({
            key: Date.now(),
            id: parsed.productId && !Number.isNaN(parsed.productId) ? parsed.productId : null,
            siteName: String(m?.[1] || '').trim(),
            productName: String(m?.[2] || '').trim(),
            type,
        });
    };

    const updateUserPermission = (userId, key, checked) => {
        const target = permissionUsers.find((user) => user.id === userId);
        if (!target) {
            return;
        }

        const nextPermissions = {
            ...target.permissions,
            [key]: checked,
        };

        setPermissionUsers((prev) => prev.map((user) => (
            user.id === userId ? { ...user, permissions: nextPermissions } : user
        )));
        setPermissionError('');
    };

    const saveUserPermissions = async (userId, permissions) => {
        const response = await fetch(`${apiBaseUrl}/users/${userId}/permissions`, {
            method: 'PUT',
            headers: JSON_HEADERS,
            body: JSON.stringify({
                requesterName: currentUser.name,
                permissions
            })
        });
        if (!response.ok) {
            throw new Error('권한 저장 실패');
        }
    };

    const deleteUserAccount = async (userId) => {
        const payload = JSON.stringify({
            requesterName: currentUser.name,
        });
        try {
            const response = await fetch(`${apiBaseUrl}/users/${userId}`, {
                method: 'DELETE',
                headers: JSON_HEADERS,
                body: payload,
            });
            await readJsonResponse(response);
        } catch (error) {
            // 운영 환경에서 DELETE가 차단되면 POST 대체 경로로 재시도
            const fallback = await fetch(`${apiBaseUrl}/users/${userId}/delete`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: payload,
            });
            await readJsonResponse(fallback);
        }
    };

    const requestMarkUserForDelete = (userId) => {
        if (userId == null) {
            return;
        }
        if (userId === currentUser?.id) {
            window.alert('본인 계정은 삭제할 수 없습니다.');
            return;
        }
        if (!window.confirm('이 사용자를 삭제하시겠습니까?\n저장 버튼을 눌러야 최종 반영됩니다.')) {
            return;
        }
        setPendingDeleteUserIds((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
        setPermissionError('');
    };

    const cancelPendingDeleteUser = (userId) => {
        setPendingDeleteUserIds((prev) => prev.filter((id) => id !== userId));
        setPermissionError('');
    };

    const handleSavePermissions = async () => {
        if (!currentUser?.name) {
            return;
        }
        const toKey = (permissions = {}) => JSON.stringify({
            canRegisterProduct: !!permissions.canRegisterProduct,
            canReviewList: !!permissions.canReviewList,
            canEditProduct: !!permissions.canEditProduct,
            canViewDetail: !!permissions.canViewDetail,
            canDownloadFirmware: !!permissions.canDownloadFirmware,
            canDownloadWiringDiagram: !!permissions.canDownloadWiringDiagram,
            canManagePermissions: !!permissions.canManagePermissions,
            canViewLogs: !!permissions.canViewLogs,
            canManageModelSpecList: !!permissions.canManageModelSpecList,
            canManageAsRepair: !!permissions.canManageAsRepair,
        });

        const pendingIds = [...pendingDeleteUserIds];
        const baseMap = new Map(initialPermissionUsers.map((u) => [u.id, toKey(u.permissions)]));
        const changedUsers = permissionUsers.filter(
            (u) => !pendingIds.includes(u.id) && baseMap.get(u.id) !== toKey(u.permissions)
        );

        if (changedUsers.length === 0 && pendingIds.length === 0) {
            window.alert('변경된 권한이나 삭제 예정인 사용자가 없습니다.');
            setPermissionError('');
            return;
        }

        setIsSavingPermissions(true);
        setPermissionError('');
        try {
            await Promise.all(pendingIds.map((id) => deleteUserAccount(id)));
            await Promise.all(changedUsers.map((user) => saveUserPermissions(user.id, user.permissions)));
            setPendingDeleteUserIds([]);
            await loadPermissionUsers();
            const currentUserUpdated = changedUsers.find((user) => user.id === currentUser?.id);
            if (currentUserUpdated) {
                const nextCurrentUser = {
                    ...currentUser,
                    permissions: { ...currentUserUpdated.permissions },
                };
                setCurrentUser(nextCurrentUser);
                localStorage.setItem('bns-user', JSON.stringify(nextCurrentUser));
            }
            window.alert(pendingIds.length > 0 && changedUsers.length > 0
                ? '사용자 삭제 및 권한 변경이 반영되었습니다.'
                : pendingIds.length > 0
                    ? '사용자 삭제가 반영되었습니다.'
                    : '권한이 저장되었습니다.');
        } catch (error) {
            setPermissionError(error?.message || '저장에 실패했습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            setIsSavingPermissions(false);
        }
    };

    const handleUpdateModel = async (model) => {
        try {
            const response = await fetch(`${apiBaseUrl}/products/${model.id}`, {
                method: 'PUT',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    siteName: model.siteName,
                    productName: model.name,
                    householdCount: model.householdCount || '',
                    type: model.type,
                    specs: model.specs,
                    sido: model.sido || '',
                    gugun: model.gugun || '',
                    dong: model.dong || '',
                    detailAddress: model.detailAddress || '',
                    actorName: currentUser?.name || null,
                    listScope: activePage === 'modelSpecList' ? PRODUCT_LIST_SCOPE_MODEL_SPEC : PRODUCT_LIST_SCOPE_MAIN,
                })
            });
            await readJsonResponse(response);

            await (activePage === 'modelSpecList' ? loadModelSpecListProducts() : loadProducts());
            return { ok: true };
        } catch (error) {
            const message = error?.message || '수정 저장에 실패했습니다. 잠시 후 다시 시도해주세요.';
            if (activePage === 'modelSpecList') {
                setModelSpecListError(message);
            } else {
                setLoadError(message);
            }
            return { ok: false, message };
        }
    };

    const handleDeleteModel = async (modelId) => {
        try {
            const listQs = activePage === 'modelSpecList'
                ? `actorName=${encodeURIComponent(currentUser?.name || '')}&listScope=${encodeURIComponent(PRODUCT_LIST_SCOPE_MODEL_SPEC)}`
                : `actorName=${encodeURIComponent(currentUser?.name || '')}&listScope=${encodeURIComponent(PRODUCT_LIST_SCOPE_MAIN)}`;
            const response = await fetch(`${apiBaseUrl}/products/${modelId}?${listQs}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error('삭제에 실패했습니다.');
            }

            await (activePage === 'modelSpecList' ? loadModelSpecListProducts() : loadProducts());
            return true;
        } catch (error) {
            if (activePage === 'modelSpecList') {
                setModelSpecListError('삭제에 실패했습니다. 잠시 후 다시 시도해주세요.');
            } else {
                setLoadError('삭제 요청에 실패했습니다. 잠시 후 다시 시도해주세요.');
            }
            return false;
        }
    };

    const handleToggleBulkFirmwareMode = () => {
        if (bulkFirmwareMode) {
            setBulkFirmwareMode(false);
            setBulkFirmwareFile(null);
            setBulkFirmwareMemo('');
            setBulkFirmwareTargetIds([]);
            return;
        }
        setBulkFirmwareMode(true);
        setBulkFirmwareFile(null);
        setBulkFirmwareMemo('');
        setBulkFirmwareTargetIds([]);
        setLoadError('');
    };

    const handleBulkFirmwareFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }
        if (!file.name.toLowerCase().endsWith('.hex')) {
            showAppToast('.HEX 파일만 업로드할 수 있습니다.', 'error');
            e.target.value = '';
            return;
        }
        setBulkFirmwareFile(file);
        e.target.value = '';
    };

    const handleBulkFirmwareRowSelect = (model, checked) => {
        const reviewStatus = String(model?.reviewStatus || '').toLowerCase();
        if (checked && reviewStatus === 'pending') {
            showAppToast('이미 승인 검토중인 리스트는 선택할 수 없습니다.', 'error');
            return;
        }
        setBulkFirmwareTargetIds((prev) => (
            checked ? (prev.includes(model.id) ? prev : [...prev, model.id]) : prev.filter((id) => id !== model.id)
        ));
    };

    const handleCancelBulkFirmware = () => {
        setBulkFirmwareMode(false);
        setBulkFirmwareFile(null);
        setBulkFirmwareMemo('');
        setBulkFirmwareTargetIds([]);
    };

    const handleConfirmBulkFirmware = async () => {
        if (!currentUser?.name) {
            return;
        }
        if (!bulkFirmwareFile) {
            showAppToast('일괄 업로드할 .HEX 파일을 선택해주세요.', 'error');
            return;
        }
        if (bulkFirmwareTargetIds.length === 0) {
            showAppToast('업로드할 리스트를 1개 이상 선택해주세요.', 'error');
            return;
        }

        setIsBulkFirmwareSubmitting(true);
        try {
            const dataUrl = await readFileAsDataUrl(bulkFirmwareFile);
            const filePayload = {
                kind: 'firmware',
                name: bulkFirmwareFile.name,
                mimeType: bulkFirmwareFile.type || '',
                dataUrl,
                description: String(bulkFirmwareMemo || '').trim(),
            };

            const targets = models.filter((item) => bulkFirmwareTargetIds.includes(item.id));
            await Promise.all(targets.map(async (model) => {
                const response = await fetch(`${apiBaseUrl}/products/${model.id}`, {
                    method: 'PUT',
                    headers: JSON_HEADERS,
                    body: JSON.stringify({
                        siteName: model.siteName,
                        productName: model.name,
                        householdCount: model.householdCount || '',
                        type: model.type,
                        specs: appendFirmwareFileToSpecs(model.specs || [], filePayload),
                        sido: model.sido || '',
                        gugun: model.gugun || '',
                        dong: model.dong || '',
                        detailAddress: model.detailAddress || '',
                        actorName: currentUser.name,
                        listScope: PRODUCT_LIST_SCOPE_MAIN,
                    }),
                });
                const data = await readJsonResponse(response);
                return data;
            }));
            await Promise.all([loadProducts(), loadPendingReviewProducts(), loadMailboxUnreadCount()]);
            showAppToast('펌웨어 일괄 업데이트 요청이 등록되었습니다.', 'success');
            handleCancelBulkFirmware();
        } catch (error) {
            showAppToast(error?.message || '펌웨어 일괄 업데이트에 실패했습니다.', 'error');
        } finally {
            setIsBulkFirmwareSubmitting(false);
        }
    };

    const isFirmwareBulkPendingRow = (model) => {
        if (String(model?.reviewStatus || '').toLowerCase() !== 'pending') {
            return false;
        }
        if (String(model?.reviewEventType || '').toLowerCase() !== 'update') {
            return false;
        }
        const diffs = [
            ...(Array.isArray(model?.reviewDiffInitial) ? model.reviewDiffInitial : []),
            ...(Array.isArray(model?.reviewDiffResubmit) ? model.reviewDiffResubmit : []),
            ...(Array.isArray(model?.reviewDiff) ? model.reviewDiff : []),
        ];
        return diffs.length > 0 && diffs.every((item) => String(item?.field || '') === `spec:${FIRMWARE_SPEC_TITLE}`);
    };

    const getBulkPendingFirmwareInfo = (model) => {
        const diffs = [
            ...(Array.isArray(model?.reviewDiffResubmit) ? model.reviewDiffResubmit : []),
            ...(Array.isArray(model?.reviewDiffInitial) ? model.reviewDiffInitial : []),
            ...(Array.isArray(model?.reviewDiff) ? model.reviewDiff : []),
        ];
        const firmwareDiff = diffs.find((item) => String(item?.field || '') === `spec:${FIRMWARE_SPEC_TITLE}`);
        const raw = String(firmwareDiff?.after || '').trim();
        if (!raw) {
            return { fileName: '-', memo: '-' };
        }
        const firstEntry = raw.split(',')[0].trim();
        const dividerIdx = firstEntry.indexOf(' - ');
        if (dividerIdx < 0) {
            return { fileName: firstEntry || '-', memo: '-' };
        }
        const fileName = firstEntry.slice(0, dividerIdx).trim() || '-';
        const memo = firstEntry.slice(dividerIdx + 3).trim() || '-';
        return { fileName, memo };
    };

    const handleToggleBulkPendingSelect = (id, checked) => {
        setBulkPendingSelectedIds((prev) => (
            checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((item) => item !== id)
        ));
    };

    const handleApproveBulkFirmwarePending = async () => {
        if (!currentUser?.name || bulkPendingSelectedIds.length === 0) {
            return;
        }
        setIsBulkPendingApproving(true);
        setPendingReviewError('');
        try {
            await Promise.all(bulkPendingSelectedIds.map((id) => approveProductRequest(id)));
            await Promise.all([loadProducts(), loadPendingReviewProducts(), loadMailboxUnreadCount()]);
            showAppToast('펌웨어 일괄 업로드 리스트 승인 완료되었습니다.', 'success');
            setBulkPendingSelectedIds([]);
            setShowBulkFirmwarePendingPopup(false);
        } catch (error) {
            setPendingReviewError(error?.message || '일괄 승인 처리에 실패했습니다.');
        } finally {
            setIsBulkPendingApproving(false);
        }
    };

    const handleRejectBulkFirmwarePending = async () => {
        if (!currentUser?.name || bulkPendingSelectedIds.length === 0) {
            return;
        }
        if (!bulkPendingRejectReason.trim()) {
            setPendingReviewError('승인 거부 내용을 입력해주세요.');
            return;
        }
        setIsBulkPendingRejecting(true);
        setPendingReviewError('');
        try {
            await Promise.all(
                bulkPendingSelectedIds.map((id) => rejectProductRequest(id, bulkPendingRejectReason))
            );
            await Promise.all([loadProducts(), loadPendingReviewProducts(), loadMailboxUnreadCount()]);
            showAppToast('펌웨어 일괄 업로드 리스트 승인 거부 완료되었습니다.', 'success');
            setBulkPendingSelectedIds([]);
            setBulkPendingRejectReason('');
            setShowBulkFirmwarePendingPopup(false);
        } catch (error) {
            setPendingReviewError(error?.message || '일괄 승인 거부 처리에 실패했습니다.');
        } finally {
            setIsBulkPendingRejecting(false);
        }
    };

    const handleApproveProduct = async (productId) => {
        if (!currentUser?.name) {
            return;
        }

        try {
            await approveProductRequest(productId);
            await Promise.all([loadProducts(), loadPendingReviewProducts(), loadMailboxUnreadCount()]);
            showAppToast('승인 완료되었습니다.', 'success');
        } catch (error) {
            setPendingReviewError(error?.message || '승인 처리에 실패했습니다. 잠시 후 다시 시도해주세요.');
        }
    };

    const requestRejectProduct = (productId) => {
        setRejectTargetId(productId);
        setRejectReason('');
        setPendingReviewError('');
    };

    const closeRejectModal = () => {
        setRejectTargetId(null);
        setRejectReason('');
        setIsRejecting(false);
    };

    const handleRejectProduct = async () => {
        if (!rejectTargetId || !currentUser?.name || !rejectReason.trim()) {
            return;
        }
        setIsRejecting(true);
        try {
            await rejectProductRequest(rejectTargetId, rejectReason.trim());
            await Promise.all([loadProducts(), loadPendingReviewProducts(), loadMailboxUnreadCount()]);
            closeRejectModal();
            showAppToast('승인 거부 처리되었습니다.', 'success');
        } catch (error) {
            setPendingReviewError(error.message || '승인 거부 처리에 실패했습니다. 잠시 후 다시 시도해주세요.');
            setIsRejecting(false);
        }
    };

    const loadLogs = async () => {
        if (!canViewLogs || !currentUser?.name) {
            return;
        }

        setLogError('');
        setIsLogLoading(true);

        try {
            const response = await fetch(`${apiBaseUrl}/logs?requesterName=${encodeURIComponent(currentUser.name)}&limit=200`);
            if (!response.ok) {
                throw new Error('로그 조회 실패');
            }

            const data = await response.json();
            setLogs(Array.isArray(data) ? data : []);
        } catch (error) {
            setLogError('로그를 불러오지 못했습니다.');
        } finally {
            setIsLogLoading(false);
        }
    };

    const refreshCurrentPage = async () => {
        setLoadError('');
        setModelSpecListError('');
        setPendingReviewError('');
        setMailboxError('');
        setPermissionError('');
        setLogError('');

        if (activePage === 'list') {
            await loadProducts();
            return;
        }
        if (activePage === 'modelSpecList') {
            setModelSpecListError('');
            await loadModelSpecListProducts();
            return;
        }
        if (activePage === 'review' && canReviewList) {
            await Promise.all([loadPendingReviewProducts(), loadProducts()]);
            return;
        }
        if (activePage === 'message') {
            await Promise.all([loadMailboxItems(), loadMailboxUnreadCount()]);
            return;
        }
        if (activePage === 'permissions' && canManagePermissions) {
            await loadPermissionUsers();
            return;
        }
        if (activePage === 'logs' && canViewLogs) {
            await loadLogs();
            return;
        }
        if (activePage === 'dropdowns' && canManageSpecDropdowns) {
            await loadSpecDropdownOptions();
            return;
        }
        if (
            (activePage === 'productRegisterSettings' && canManagePermissions)
            || (activePage === 'qtyChartSettings' && canManageAsRepair)
        ) {
            await loadProductRegistrationConfig();
            return;
        }
        if ((activePage === 'asRepair' || activePage === 'asRepairAdd' || activePage === 'asRepairPersonal' || activePage === 'asRepairPersonalAdd') && canManageAsRepair) {
            return;
        }

        // Fallback refresh for other pages.
        await loadProducts();
    };

    const updateSpecFilter = (index, field, value) => {
        setSpecFilters((prev) => prev.map((filter, filterIndex) => (
            filterIndex === index ? { ...filter, [field]: value } : filter
        )));
    };

    const toggleListTypeFilter = (type) => {
        setListTypeFilters((prev) => {
            const bothSelected = prev.adjuster && prev.controller;

            if (bothSelected) {
                return type === 'adjuster'
                    ? { adjuster: true, controller: false }
                    : { adjuster: false, controller: true };
            }

            if (type === 'adjuster') {
                return prev.adjuster
                    ? { adjuster: true, controller: true }
                    : { adjuster: true, controller: false };
            }

            return prev.controller
                ? { adjuster: true, controller: true }
                : { adjuster: false, controller: true };
        });
    };

    const specTitleOptions = useMemo(() => {
        const pinnedOrder = ['현장명', '모델명', '시/도', '구/군'];
        const sourceModels = activePage === 'modelSpecList' ? modelSpecListModels : models;
        const fromSpecs = Array.from(new Set(
            sourceModels
                .flatMap((model) => (model.specs || []).map((spec) => spec.title))
                .filter((title) => title && !EXCLUDED_FILTER_TITLES.has(title) && !pinnedOrder.includes(title))
        ));
        return [...pinnedOrder, ...fromSpecs];
    }, [activePage, modelSpecListModels, models]);

    const filteredModels = useMemo(() => {
        const normalizedQuery = searchQuery.trim().toLowerCase();
        if (!normalizedQuery) {
            return models;
        }

        return models.filter((model) => {
            const target = [
                model.siteName,
                model.name,
                model.type === 'adjuster' ? '조절기' : '제어기',
                ...(model.specs || []).map((spec) => spec.details)
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return target.includes(normalizedQuery);
        });
    }, [models, searchQuery]);

    const specFilteredModels = useMemo(() => filteredModels.filter((model) => (
        specFilters.every((filter) => {
            const title = filter.title.trim();
            const query = filter.query.trim().toLowerCase();

            if (!title || !query) {
                return true;
            }

            if (title === '현장명') {
                return String(model.siteName || '').toLowerCase().includes(query);
            }

            if (title === '모델명') {
                return String(model.name || '').toLowerCase().includes(query);
            }

            if (title === '시/도') {
                return String(model.sido || '').toLowerCase().includes(query);
            }

            if (title === '구/군') {
                return String(model.gugun || '').toLowerCase().includes(query);
            }

            const matchedSpec = (model.specs || []).find((spec) => spec.title === title);
            return matchedSpec ? String(matchedSpec.details || '').toLowerCase().includes(query) : false;
        })
    )), [filteredModels, specFilters]);

    const typeFilteredModels = useMemo(() => (
        specFilteredModels.filter((model) => listTypeFilters[model.type] !== false)
    ), [specFilteredModels, listTypeFilters]);

    const reviewFilteredModels = useMemo(() => {
        const requesterName = String(currentUser?.name || '').trim();
        return typeFilteredModels.filter((model) => {
            if (model.reviewStatus !== 'rejected') {
                return true;
            }
            if (canViewDetail) {
                return true;
            }
            return String(model.pendingRequesterName || '').trim() === requesterName;
        });
    }, [typeFilteredModels, currentUser?.name, canViewDetail]);

    const displayedModels = useMemo(() => {
        if (!mapAddressFilter) {
            return reviewFilteredModels;
        }
        return reviewFilteredModels.filter((model) => (
            String(model.sido || '').trim() === String(mapAddressFilter.sido || '').trim()
            && String(model.gugun || '').trim() === String(mapAddressFilter.gugun || '').trim()
            && String(model.detailAddress || '').trim() === String(mapAddressFilter.detailAddress || '').trim()
        ));
    }, [reviewFilteredModels, mapAddressFilter]);

    const displayedModelsWithAssets = useMemo(
        () => displayedModels.map((model) => enrichModelWithLoadedAssets(model, PRODUCT_LIST_SCOPE_MAIN)),
        [displayedModels, enrichModelWithLoadedAssets],
    );

    const modelSpecFilteredModels = useMemo(() => {
        const normalizedQuery = searchQuery.trim().toLowerCase();
        if (!normalizedQuery) {
            return modelSpecListModels;
        }
        return modelSpecListModels.filter((model) => {
            const target = [
                model.siteName,
                model.name,
                model.type === 'adjuster' ? '조절기' : '제어기',
                ...(model.specs || []).map((spec) => spec.details)
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return target.includes(normalizedQuery);
        });
    }, [modelSpecListModels, searchQuery]);

    const modelSpecSpecFilteredModels = useMemo(() => modelSpecFilteredModels.filter((model) => (
        specFilters.every((filter) => {
            const title = filter.title.trim();
            const query = filter.query.trim().toLowerCase();
            if (!title || !query) {
                return true;
            }
            if (title === '현장명') {
                return String(model.siteName || '').toLowerCase().includes(query);
            }
            if (title === '모델명') {
                return String(model.name || '').toLowerCase().includes(query);
            }
            if (title === '시/도') {
                return String(model.sido || '').toLowerCase().includes(query);
            }
            if (title === '구/군') {
                return String(model.gugun || '').toLowerCase().includes(query);
            }
            const matchedSpec = (model.specs || []).find((spec) => spec.title === title);
            return matchedSpec ? String(matchedSpec.details || '').toLowerCase().includes(query) : false;
        })
    )), [modelSpecFilteredModels, specFilters]);

    const modelSpecTypeFilteredModels = useMemo(() => (
        modelSpecSpecFilteredModels.filter((model) => listTypeFilters[model.type] !== false)
    ), [modelSpecSpecFilteredModels, listTypeFilters]);

    const modelSpecReviewFilteredModels = useMemo(() => {
        const requesterName = String(currentUser?.name || '').trim();
        return modelSpecTypeFilteredModels.filter((model) => {
            if (model.reviewStatus !== 'rejected') {
                return true;
            }
            if (canViewDetail) {
                return true;
            }
            return String(model.pendingRequesterName || '').trim() === requesterName;
        });
    }, [modelSpecTypeFilteredModels, currentUser?.name, canViewDetail]);

    const modelSpecDisplayedModels = useMemo(
        () => modelSpecReviewFilteredModels,
        [modelSpecReviewFilteredModels],
    );

    const modelSpecDisplayedModelsWithAssets = useMemo(
        () => modelSpecDisplayedModels.map((model) => enrichModelWithLoadedAssets(model, PRODUCT_LIST_SCOPE_MODEL_SPEC)),
        [modelSpecDisplayedModels, enrichModelWithLoadedAssets],
    );

    const pendingReviewProductsWithAssets = useMemo(
        () => pendingReviewProducts.map((model) => enrichModelWithLoadedAssets(model, PRODUCT_LIST_SCOPE_REVIEW)),
        [pendingReviewProducts, enrichModelWithLoadedAssets],
    );

    const bulkFirmwarePendingRows = useMemo(
        () => pendingReviewProducts.filter((item) => isFirmwareBulkPendingRow(item)),
        [pendingReviewProducts]
    );

    const bulkFirmwarePendingGroups = useMemo(() => {
        const groups = [];
        const byKey = new Map();
        bulkFirmwarePendingRows.forEach((item) => {
            const info = getBulkPendingFirmwareInfo(item);
            const key = `${info.fileName}||${info.memo}`;
            if (!byKey.has(key)) {
                const nextGroup = { key, fileName: info.fileName, memo: info.memo, items: [] };
                byKey.set(key, nextGroup);
                groups.push(nextGroup);
            }
            byKey.get(key).items.push(item);
        });
        return groups;
    }, [bulkFirmwarePendingRows]);

    const handleAddressMarkerSelect = useCallback((selection) => {
        setMapAddressFilter(selection);
        if (!selection) {
            setExpandFromMapRequest(null);
            return;
        }
        window.requestAnimationFrame(() => {
            document.getElementById('spec-list-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        const matches = reviewFilteredModels.filter((model) => (
            String(model.sido || '').trim() === String(selection.sido || '').trim()
            && String(model.gugun || '').trim() === String(selection.gugun || '').trim()
            && String(model.detailAddress || '').trim() === String(selection.detailAddress || '').trim()
        ));
        const first = matches[0];
        setExpandFromMapRequest(first ? { key: Date.now(), modelId: first.id } : null);
    }, [reviewFilteredModels]);

    const formatLogTarget = (log) => {
        if (!log) {
            return '-';
        }

        const actionTargetMap = {
            login: '로그인',
            logout: '로그아웃',
            login_failed: '로그인 실패',
            signup: '회원가입',
            signup_failed: '회원가입 실패',
            list_detail_view: '세부사항 열람',
            list_page_move: '리스트 페이지 이동',
            product_create: '제품 등록',
            product_update: '제품 수정',
            product_delete_request: '제품 삭제 요청',
            product_delete_approve: '제품 삭제 승인',
            product_approve: '리스트 승인',
            product_reject: '리스트 승인 거부',
            permission_update: '권한 변경',
            user_delete: '사용자 삭제',
        };

        if (actionTargetMap[log.actionType]) {
            return actionTargetMap[log.actionType];
        }
        if (!log.targetType) {
            return '-';
        }
        return `${log.targetType}${log.targetId ? `#${log.targetId}` : ''}`;
    };

    const permissionKeyLabels = {
        canManagePermissions: '권한 관리 권한',
        canReviewList: '검토 권한',
        canViewLogs: '로그 관리 권한',
        canEditProduct: '리스트 수정 권한',
        canViewDetail: '세부사항 열람 권한',
        canDownloadFirmware: '펌웨어 다운로드 권한',
        canDownloadWiringDiagram: '결선도 다운로드 권한',
        canRegisterProduct: '제품 등록 권한',
        canManageModelSpecList: '모델별 사양 관리 권한',
        canManageAsRepair: 'A/S 수리 관리 권한',
    };

    const LOG_LABEL_MAX_LEN = 10;
    const truncateLogLabel = (value) => {
        const s = String(value || '').trim();
        if (!s) {
            return '-';
        }
        if (s.length <= LOG_LABEL_MAX_LEN) {
            return s;
        }
        return `${s.slice(0, LOG_LABEL_MAX_LEN)}…`;
    };

    const formatLogDescription = (log) => {
        const siteName = log?.metadata?.siteName || '';
        const productName = log?.metadata?.productName || '';
        const hasProductMeta = !!siteName || !!productName;
        const toProductSummary = () => `현장명:${truncateLogLabel(siteName)} 모델명:${truncateLogLabel(productName)}`;
        const forceProductSummaryActions = new Set([
            'product_approve',
            'product_reject',
            'product_delete_request',
            'product_delete_approve',
        ]);

        if (forceProductSummaryActions.has(log?.actionType)) {
            return toProductSummary();
        }

        if (['list_detail_view', 'product_create', 'product_update', 'product_delete_request', 'product_delete_approve', 'product_approve', 'product_reject'].includes(log?.actionType) && hasProductMeta) {
            return toProductSummary();
        }

        if (log?.actionType === 'permission_update') {
            const targetUserName = String(log?.metadata?.targetUserName || '').trim();
            const changedKeys = Array.isArray(log?.metadata?.changedPermissionKeys)
                ? log.metadata.changedPermissionKeys
                : [];

            if (changedKeys.length > 0) {
                const changedLabel = changedKeys.map((key) => permissionKeyLabels[key] || key).join(', ');
                return `${targetUserName || '-'}: ${changedLabel}`;
            }
        }

        return log?.description || '-';
    };

    const formatLogDescriptionTooltip = (log) => {
        const siteName = log?.metadata?.siteName || '';
        const productName = log?.metadata?.productName || '';
        const hasProductMeta = !!siteName || !!productName;
        const forceProductSummaryActions = new Set([
            'product_approve',
            'product_reject',
            'product_delete_request',
            'product_delete_approve',
        ]);
        const usesProductSummary = forceProductSummaryActions.has(log?.actionType)
            || (['list_detail_view', 'product_create', 'product_update', 'product_delete_request', 'product_delete_approve', 'product_approve', 'product_reject'].includes(log?.actionType) && hasProductMeta);
        if (!usesProductSummary) {
            return undefined;
        }
        return `현장명:${String(siteName || '').trim() || '-'} 모델명:${String(productName || '').trim() || '-'}`;
    };

    const formatLogUser = (log) => {
        if (!log?.userName) {
            return '-';
        }

        return `${log.userName}(${log.userPhoneNumber || '-'})`;
    };

    const formatKoreanDateTime = (value) => {
        if (!value) {
            return '-';
        }
        const raw = String(value).trim();
        let date;
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)) {
            // MySQL 등에서 오는 "공백" 구분 문자열은 KST 벽시계로 해석(타임존 미표기 대응)
            date = new Date(`${raw.replace(' ', 'T')}+09:00`);
        } else {
            date = new Date(raw);
        }

        if (Number.isNaN(date.getTime())) {
            return raw;
        }

        return new Intl.DateTimeFormat('ko-KR', {
            timeZone: 'Asia/Seoul',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        }).format(date);
    };

    useEffect(() => {
        if (isAuthenticated && canManagePermissions && activePage === 'permissions') {
            loadPermissionUsers();
        }
    }, [isAuthenticated, canManagePermissions, activePage]);

    useEffect(() => {
        if (activePage !== 'permissions') {
            setPendingDeleteUserIds([]);
        }
    }, [activePage]);

    useEffect(() => {
        setBulkFirmwareTargetIds((prev) => prev.filter((id) => models.some((m) => m.id === id)));
    }, [models]);

    useEffect(() => {
        if (!showBulkFirmwarePendingPopup) {
            setBulkPendingSelectedIds([]);
            setBulkPendingRejectReason('');
        }
    }, [showBulkFirmwarePendingPopup]);

    useEffect(() => {
        if (isAuthenticated && canViewLogs && activePage === 'logs') {
            loadLogs();
        }
    }, [isAuthenticated, canViewLogs, activePage]);

    useEffect(() => {
        if (isAuthenticated && canReviewList && activePage === 'review') {
            loadPendingReviewProducts();
        }
    }, [isAuthenticated, canReviewList, activePage]);

    useEffect(() => {
        if (isAuthenticated && currentUser?.name) {
            loadMailboxUnreadCount();
        }
    }, [isAuthenticated, currentUser?.name]);

    useEffect(() => {
        if (isAuthenticated && activePage === 'message') {
            loadMailboxItems();
            markAllMailboxAsRead();
        }
    }, [isAuthenticated, activePage]);

    const handlePasswordChangeSubmit = async (e) => {
        e.preventDefault();
        if (!currentUser?.name) {
            setPasswordChangeStatus('로그인 정보가 없습니다. 다시 로그인 후 시도해주세요.');
            return;
        }
        const currentPassword = passwordChangeForm.currentPassword.trim();
        const newPassword = passwordChangeForm.newPassword.trim();
        const confirmPassword = passwordChangeForm.confirmPassword.trim();

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordChangeStatus('현재 비밀번호와 새 비밀번호를 모두 입력해주세요.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordChangeStatus('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
            return;
        }

        try {
            setPasswordChangeStatus('비밀번호 변경 중입니다...');
            const response = await fetch(`${apiBaseUrl}/change-password`, {
                method: 'POST',
                headers: JSON_HEADERS,
                body: JSON.stringify({
                    username: currentUser.name,
                    currentPassword,
                    newPassword,
                }),
            });
            const data = await readJsonResponse(response);
            if (data?.ok) {
                setPasswordChangeStatus('비밀번호가 성공적으로 변경되었습니다. 다음 로그인부터 새 비밀번호를 사용해주세요.');
                setPasswordChangeForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                });
            } else {
                setPasswordChangeStatus('비밀번호 변경에 실패했습니다.');
            }
        } catch (error) {
            setPasswordChangeStatus(error?.message || '비밀번호 변경 중 오류가 발생했습니다.');
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="login-page">
                {authMode === 'login' ? (
                    <form className="login-card" onSubmit={handleLogin}>
                        <h2>로그인</h2>
                        <input
                            className="login-input"
                            type="text"
                            placeholder="이름"
                            value={loginForm.name}
                            onChange={(e) => setLoginForm((prev) => ({ ...prev, name: e.target.value }))}
                        />
                        <input
                            className="login-input"
                            type="password"
                            placeholder="비밀번호"
                            value={loginForm.password}
                            onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))}
                        />
                        {loginError && <div className="login-error">{loginError}</div>}
                        {signupSuccess && <div className="login-success">{signupSuccess}</div>}
                        <button type="submit" className="button login-button" disabled={isLoggingIn}>
                            {isLoggingIn ? '로그인 중...' : '로그인'}
                        </button>
                        <div className="auth-link-row">
                            <span className="auth-help-text">비밀번호 분실시 관리자에게 문의바랍니다</span>
                            <button type="button" className="auth-link-button" onClick={() => setAuthMode('signup')}>회원가입</button>
                        </div>
                    </form>
                ) : (
                    <form className="login-card" onSubmit={handleSignup}>
                        <h2>회원가입</h2>
                        <input
                            className="login-input"
                            type="text"
                            placeholder="이름"
                            value={signupForm.name}
                            onChange={(e) => setSignupForm((prev) => ({ ...prev, name: e.target.value }))}
                        />
                        <input
                            className="login-input"
                            type="text"
                            placeholder="전화번호"
                            value={signupForm.phoneNumber}
                            onChange={(e) => setSignupForm((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                        />
                        <input
                            className="login-input"
                            type="password"
                            placeholder="비밀번호"
                            value={signupForm.password}
                            onChange={(e) => setSignupForm((prev) => ({ ...prev, password: e.target.value }))}
                        />
                        <input
                            className="login-input"
                            type="password"
                            placeholder="비밀번호 확인"
                            value={signupForm.confirmPassword}
                            onChange={(e) => setSignupForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                        />
                        {signupError && <div className="login-error">{signupError}</div>}
                        <button type="submit" className="button login-button" disabled={isSigningUp}>
                            {isSigningUp ? '가입 중...' : '회원가입'}
                        </button>
                        <div className="auth-link-row">
                            <button type="button" className="auth-link-button" onClick={() => setAuthMode('login')}>로그인으로 돌아가기</button>
                        </div>
                    </form>
                )}
            </div>
        );
    }

    return (
        <div className={`App${darkMode ? ' dark-mode' : ''}`}>
            <div className="app-topbar">
                <h1>Boiler Specification Management</h1>
                <button className="button top-refresh-button" onClick={refreshCurrentPage}>새로고침</button>
            </div>
            {!activeFormType && (
                <div className="workspace-layout">
                    <aside className="sidebar">
                        {currentUser?.name && (
                            <div className="sidebar-greeting-block">
                                <div className="sidebar-greeting">안녕하세요 {currentUser.name}님</div>
                                <button
                                    type="button"
                                    className="sidebar-password-change-link"
                                    onClick={() => {
                                        setShowPasswordChange((prev) => !prev);
                                        setPasswordChangeStatus('');
                                    }}
                                >
                                    비밀번호 변경
                                </button>
                                {showPasswordChange && (
                                    <form
                                        className="sidebar-password-change-form"
                                        onSubmit={handlePasswordChangeSubmit}
                                    >
                                        <input
                                            type="password"
                                            className="sidebar-password-input"
                                            placeholder="현재 비밀번호"
                                            value={passwordChangeForm.currentPassword}
                                            onChange={(e) => setPasswordChangeForm((prev) => ({
                                                ...prev,
                                                currentPassword: e.target.value,
                                            }))}
                                        />
                                        <input
                                            type="password"
                                            className="sidebar-password-input"
                                            placeholder="새 비밀번호"
                                            value={passwordChangeForm.newPassword}
                                            onChange={(e) => setPasswordChangeForm((prev) => ({
                                                ...prev,
                                                newPassword: e.target.value,
                                            }))}
                                        />
                                        <input
                                            type="password"
                                            className="sidebar-password-input"
                                            placeholder="새 비밀번호 확인"
                                            value={passwordChangeForm.confirmPassword}
                                            onChange={(e) => setPasswordChangeForm((prev) => ({
                                                ...prev,
                                                confirmPassword: e.target.value,
                                            }))}
                                        />
                                        {passwordChangeStatus && (
                                            <div className="sidebar-password-change-status">
                                                {passwordChangeStatus}
                                            </div>
                                        )}
                                        <div className="sidebar-password-change-actions">
                                            <button type="submit" className="button sidebar-password-change-button">
                                                변경 저장
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        )}
                        <h3>메뉴</h3>
                        <button className={`sidebar-menu-button sidebar-menu-list-button ${activePage === 'list' ? 'active' : ''}`} onClick={() => goToPage('list')}>리스트</button>
                        <button className={`sidebar-menu-button ${activePage === 'message' ? 'active' : ''}`} onClick={() => goToPage('message')}>
                            알림
                            {mailboxUnreadCount > 0 && <span className="sidebar-mail-unread-dot" />}
                        </button>
                        {canReviewList && (
                            <button className={`sidebar-menu-button ${activePage === 'review' ? 'active' : ''}`} onClick={() => goToPage('review')}>리스트 승인 관리</button>
                        )}
                        {canManageModelSpecList && (
                            <button
                                type="button"
                                className={`sidebar-menu-button ${activePage === 'modelSpecList' ? 'active' : ''}`}
                                onClick={() => goToPage('modelSpecList')}
                            >
                                모델별 사양 리스트
                            </button>
                        )}
                        <button className={`sidebar-menu-button ${activePage === 'settings' ? 'active' : ''}`} onClick={() => goToPage('settings')}>설정</button>
                        {canViewLogs && (
                            <button className={`sidebar-menu-button ${activePage === 'logs' ? 'active' : ''}`} onClick={() => goToPage('logs')}>로그 관리</button>
                        )}
                        {canManagePermissions && (
                            <button
                                type="button"
                                className={`sidebar-menu-button ${activePage === 'productRegisterSettings' ? 'active' : ''}`}
                                onClick={() => goToPage('productRegisterSettings')}
                            >
                                제품등록 설정
                            </button>
                        )}
                        {canManageSpecDropdowns && (
                            <button className={`sidebar-menu-button ${activePage === 'dropdowns' ? 'active' : ''}`} onClick={() => goToPage('dropdowns')}>드롭다운 관리</button>
                        )}
                        {canManageAsRepair && (
                            <button
                                type="button"
                                className={`sidebar-menu-button ${activePage === 'asRepair' || activePage === 'asRepairAdd' ? 'active' : ''}`}
                                onClick={() => goToPage('asRepair')}
                            >
                                A/S 수리 관리
                            </button>
                        )}
                        {canManageAsRepair && (
                            <button
                                type="button"
                                className={`sidebar-menu-button ${activePage === 'asRepairPersonal' || activePage === 'asRepairPersonalAdd' ? 'active' : ''}`}
                                onClick={() => goToPage('asRepairPersonal')}
                            >
                                A/S 개인 관리
                            </button>
                        )}
                        {canManageAsRepair && (
                            <button
                                type="button"
                                className={`sidebar-menu-button ${activePage === 'qtyChartSettings' ? 'active' : ''}`}
                                onClick={() => goToPage('qtyChartSettings')}
                            >
                                그래프 관리
                            </button>
                        )}
                        {canManagePermissions && (
                            <button className={`sidebar-menu-button ${activePage === 'permissions' ? 'active' : ''}`} onClick={() => goToPage('permissions')}>권한 관리</button>
                        )}
                        <button className="sidebar-menu-button sidebar-logout-button" onClick={handleLogout}>로그아웃</button>
                    </aside>

                    <section className="page-content">
                        {(activePage === 'list' || activePage === 'modelSpecList') && (
                            <>
                                <div className="list-logo-wrap">
                                    <img
                                        className="list-company-logo"
                                        src={darkMode ? COMPANY_LOGO_DARK_URL : COMPANY_LOGO_LIGHT_URL}
                                        alt="B&S Engineering"
                                    />
                                </div>
                                {activePage === 'list' && (
                                    <KoreaRegionMap models={models} onAddressMarkerSelect={handleAddressMarkerSelect} />
                                )}
                                <div className="search-bar-wrap">
                                    <div className="search-filters-left">
                                        {specFilters.map((filter, index) => (
                                            <div key={index} className="spec-filter-group">
                                                <select
                                                    className="spec-filter-select"
                                                    value={filter.title}
                                                    onChange={(e) => updateSpecFilter(index, 'title', e.target.value)}
                                                >
                                                    <option value="">사양 선택</option>
                                                    {specTitleOptions.map((title) => (
                                                        <option key={title} value={title}>{title}</option>
                                                    ))}
                                                </select>
                                                <input
                                                    className="spec-filter-input"
                                                    type="text"
                                                    value={filter.query}
                                                    onChange={(e) => updateSpecFilter(index, 'query', e.target.value)}
                                                    placeholder="사양 값 검색"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="search-main-right">
                                        <input
                                            className="search-input"
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="현장명, 제품명, 타입, 사양 값 검색"
                                        />
                                    </div>
                                </div>

                                <div id="spec-list-anchor" className="spec-list-anchor" />

                                <div className="top-buttons">
                                    {canEditProduct && (
                                        <button className="button" onClick={openAddPageFromListFilters}>제품 등록</button>
                                    )}
                                </div>

                                {(activePage === 'list' ? loadError : modelSpecListError) && (
                                    <div className="error-box">{activePage === 'list' ? loadError : modelSpecListError}</div>
                                )}

                                {activePage === 'list' ? (
                                    isProductsListLoading && models.length === 0 ? (
                                        <p className="list-loading-message">리스트 불러오는 중...</p>
                                    ) : (
                                    <SpecList
                                        models={displayedModelsWithAssets}
                                        onUpdateModel={handleUpdateModel}
                                        onDeleteModel={handleDeleteModel}
                                        canEditProduct={canEditProduct}
                                        canViewDetail={canViewDetail}
                                        canManagePermissions={canManagePermissions}
                                        canDownloadFirmware={canDownloadFirmware}
                                        canDownloadWiringDiagram={canDownloadWiringDiagram}
                                        listSummaryModelNameOnly={false}
                                        listVariant="mainList"
                                        headerExtra={canEditProduct ? (
                                            <div className="bulk-firmware-head-wrap">
                                                {!bulkFirmwareMode && (
                                                    <button
                                                        type="button"
                                                        className="button bulk-firmware-toggle"
                                                        onClick={handleToggleBulkFirmwareMode}
                                                        disabled={isBulkFirmwareSubmitting}
                                                    >
                                                        펌웨어 일괄 업데이트
                                                    </button>
                                                )}
                                                {bulkFirmwareMode && (
                                                    <div className="bulk-firmware-inline-form">
                                                        <input
                                                            type="file"
                                                            className="field-input bulk-firmware-file-input"
                                                            accept=".hex"
                                                            onChange={handleBulkFirmwareFileChange}
                                                            disabled={isBulkFirmwareSubmitting}
                                                        />
                                                        <span className="bulk-firmware-file-name">
                                                            {bulkFirmwareFile?.name || '선택된 파일 없음'}
                                                        </span>
                                                        <textarea
                                                            className="field-input bulk-firmware-memo-input"
                                                            placeholder="펌웨어 메모"
                                                            value={bulkFirmwareMemo}
                                                            onChange={(e) => setBulkFirmwareMemo(e.target.value)}
                                                            disabled={isBulkFirmwareSubmitting}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="button bulk-firmware-confirm"
                                                            onClick={handleConfirmBulkFirmware}
                                                            disabled={isBulkFirmwareSubmitting}
                                                        >
                                                            {isBulkFirmwareSubmitting ? '처리중...' : '확인'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="button button-cancel bulk-firmware-cancel"
                                                            onClick={handleCancelBulkFirmware}
                                                            disabled={isBulkFirmwareSubmitting}
                                                        >
                                                            취소
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : null}
                                        showTypeFilter
                                        typeFilters={listTypeFilters}
                                        onToggleTypeFilter={toggleListTypeFilter}
                                        editRequest={retryEditRequest}
                                        onEditRequestHandled={(key) => {
                                            setRetryEditRequest((prev) => (prev?.key === key ? null : prev));
                                        }}
                                        currentUserName={currentUser?.name || ''}
                                        specDropdownOptions={specDropdownOptions}
                                        productRegistrationConfig={productRegistrationConfig}
                                        allowAddDropdownOption={canManageSpecDropdowns}
                                        onAddDropdownOption={handleAddSpecDropdownOption}
                                        expandFromMapRequest={expandFromMapRequest}
                                        onExpandFromMapHandled={() => setExpandFromMapRequest(null)}
                                        lazyLoadAssets
                                        assetListScope={PRODUCT_LIST_SCOPE_MAIN}
                                        onLoadModelAssets={handleLoadMainListAssets}
                                        loadingAssetModelIds={loadingAssetModelIds}
                                        enableRowSelect={bulkFirmwareMode}
                                        selectedRowIds={bulkFirmwareTargetIds}
                                        onToggleRowSelect={handleBulkFirmwareRowSelect}
                                        isRowSelectable={(model) => String(model?.reviewStatus || '').toLowerCase() !== 'pending'}
                                    />
                                    )
                                ) : isModelSpecListLoading && modelSpecListModels.length === 0 ? (
                                    <p className="list-loading-message">리스트 불러오는 중...</p>
                                ) : (
                                    <ModelSpecListPanel
                                        models={modelSpecDisplayedModelsWithAssets}
                                        onUpdateModel={handleUpdateModel}
                                        onDeleteModel={handleDeleteModel}
                                        canEditProduct={canEditProduct}
                                        canViewDetail={canViewDetail}
                                        canManagePermissions={canManagePermissions}
                                        canDownloadFirmware={canDownloadFirmware}
                                        canDownloadWiringDiagram={canDownloadWiringDiagram}
                                        headerExtra={null}
                                        showTypeFilter
                                        typeFilters={listTypeFilters}
                                        onToggleTypeFilter={toggleListTypeFilter}
                                        editRequest={retryEditRequest}
                                        onEditRequestHandled={(key) => {
                                            setRetryEditRequest((prev) => (prev?.key === key ? null : prev));
                                        }}
                                        currentUserName={currentUser?.name || ''}
                                        specDropdownOptions={specDropdownOptions}
                                        productRegistrationConfig={productRegistrationConfig}
                                        allowAddDropdownOption={canManageSpecDropdowns}
                                        onAddDropdownOption={handleAddSpecDropdownOption}
                                        expandFromMapRequest={null}
                                        onExpandFromMapHandled={() => setExpandFromMapRequest(null)}
                                        lazyLoadAssets
                                        assetListScope={PRODUCT_LIST_SCOPE_MODEL_SPEC}
                                        onLoadModelAssets={handleLoadModelSpecListAssets}
                                        loadingAssetModelIds={loadingAssetModelIds}
                                        enableRowSelect={false}
                                        selectedRowIds={[]}
                                    />
                                )}
                            </>
                        )}

                        {activePage === 'review' && canReviewList && (
                            <div className="placeholder-page">
                                <h3>리스트 승인 관리</h3>
                                {pendingReviewError && <div className="error-box">{pendingReviewError}</div>}
                                {isPendingReviewLoading ? (
                                    <p>불러오는 중...</p>
                                ) : pendingReviewProducts.length === 0 ? (
                                    <p>승인 대기 중인 리스트가 없습니다.</p>
                                ) : (
                                    <SpecList
                                        models={pendingReviewProductsWithAssets}
                                        onApproveModel={handleApproveProduct}
                                        onRejectModel={requestRejectProduct}
                                        canEditProduct={false}
                                        canViewDetail={canViewDetail}
                                        canManagePermissions={canManagePermissions}
                                        canDownloadFirmware={canDownloadFirmware}
                                        canDownloadWiringDiagram={canDownloadWiringDiagram}
                                        lazyLoadAssets
                                        assetListScope={PRODUCT_LIST_SCOPE_REVIEW}
                                        onLoadModelAssets={loadReviewProductAssets}
                                        loadingAssetModelIds={loadingAssetModelIds}
                                        headerExtra={(
                                            <button
                                                type="button"
                                                className="button bulk-firmware-pending-open"
                                                onClick={() => setShowBulkFirmwarePendingPopup(true)}
                                            >
                                                펌웨어 일괄 업로드 리스트
                                            </button>
                                        )}
                                        currentUserName={currentUser?.name || ''}
                                        allowPendingExpand
                                        showReviewMeta
                                        specDropdownOptions={specDropdownOptions}
                                        productRegistrationConfig={productRegistrationConfig}
                                    />
                                )}
                            </div>
                        )}

                        {activePage === 'message' && (
                            <div className="placeholder-page mailbox-page">
                                <div className="mailbox-page-header">
                                    <h3>알림</h3>
                                    <div className="mailbox-header-actions">
                                        <button className="button button-cancel" onClick={handleDeleteSelectedMailboxItems} disabled={isDeletingMailboxItems}>
                                            {isDeletingMailboxItems ? '삭제중...' : '삭제'}
                                        </button>
                                        <button
                                            type="button"
                                            className="mailbox-select-all-text"
                                            onClick={handleToggleSelectAllMailbox}
                                            disabled={isMailboxLoading || mailboxItems.length === 0}
                                        >
                                            {isAllMailboxSelected ? '전체 해제' : '전체 선택'}
                                        </button>
                                    </div>
                                </div>
                                {mailboxError && <div className="error-box">{mailboxError}</div>}
                                {isMailboxLoading ? (
                                    <p>불러오는 중...</p>
                                ) : mailboxItems.length === 0 ? (
                                    <p>알림이 없습니다.</p>
                                ) : (
                                    <div className="mailbox-list">
                                        {mailboxItems.map((item) => (
                                            <div key={item.id} className={`mailbox-item${item.isRead ? '' : ' unread'}`}>
                                                <div className="mailbox-item-head">
                                                    <div className="mailbox-item-title">{item.title}</div>
                                                    <div className="mailbox-item-actions">
                                                        {item.title === '리스트 승인 거부' ? (
                                                            <button className="button model-edit-button" onClick={() => handleMailboxRetryEdit(item)}>
                                                                재수정
                                                            </button>
                                                        ) : (
                                                            <AppleSwitchCheckbox
                                                                checked={selectedMailboxIds.includes(item.id)}
                                                                onChange={(e) => toggleMailboxItemSelection(item.id, e.target.checked)}
                                                                aria-label={`알림 선택: ${item.title}`}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="mailbox-item-content">
                                                    {item.title === '리스트 승인 거부' ? (() => {
                                                        const p = parseListRejectMailboxContent(item.content);
                                                        return (
                                                            <div className="mailbox-reject-two-col">
                                                                <div className="mailbox-reject-col">{p.rowMeta}</div>
                                                                <div className="mailbox-reject-col">{p.rowReason}</div>
                                                            </div>
                                                        );
                                                    })() : (
                                                        item.content
                                                    )}
                                                </div>
                                                <div className="mailbox-item-time">{new Date(item.createdAt).toLocaleString()}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}


                        {activePage === 'dropdowns' && canManageSpecDropdowns && (
                            <DropdownManagePage
                                currentUserName={currentUser?.name || ''}
                                specDropdownOptions={specDropdownOptions}
                                productRegistrationConfig={productRegistrationConfig}
                                onOptionsChange={setSpecDropdownOptions}
                                showAppToast={showAppToast}
                            />
                        )}

                        {activePage === 'productRegisterSettings' && canManagePermissions && (
                            <ProductRegistrationSettingsPage
                                currentUserName={currentUser?.name || ''}
                                initialConfig={productRegistrationConfig}
                                initialAsRepairConfig={asRepairFormConfig}
                                initialAsRepairPersonalConfig={asRepairPersonalFormConfig}
                                onConfigSaved={handleRegistrationSettingsSaved}
                                showAppToast={showAppToast}
                            />
                        )}

                        {activePage === 'asRepair' && canManageAsRepair && (
                            <AsRepairManagePage
                                canManageAsRepair={canManageAsRepair}
                                currentUserName={currentUser?.name || ''}
                                asRepairFormConfig={asRepairFormConfig}
                                productModels={models}
                                managementScope={AS_REPAIR_MANAGEMENT_SCOPE.REPAIR}
                                pageTitle="A/S 수리 관리"
                                showAppToast={showAppToast}
                                hideAppToast={hideAppToast}
                                onNavigateToAdd={() => goToPage('asRepairAdd')}
                            />
                        )}

                        {activePage === 'asRepairPersonal' && canManageAsRepair && (
                            <AsRepairManagePage
                                canManageAsRepair={canManageAsRepair}
                                currentUserName={currentUser?.name || ''}
                                asRepairFormConfig={asRepairPersonalFormConfig}
                                qtyChartConfig={asRepairQtyChartConfig}
                                listExportConfig={asRepairListExportConfig}
                                productModels={models}
                                managementScope={AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL}
                                pageTitle="A/S 개인 관리"
                                showAppToast={showAppToast}
                                hideAppToast={hideAppToast}
                                onNavigateToAdd={() => goToPage('asRepairPersonalAdd')}
                            />
                        )}

                        {activePage === 'qtyChartSettings' && canManageAsRepair && (
                            <AsRepairQtyChartSettingsPage
                                currentUserName={currentUser?.name || ''}
                                asRepairFormConfig={asRepairPersonalFormConfig}
                                initialChartConfig={asRepairQtyChartConfig}
                                onChartConfigSaved={(next) => setAsRepairQtyChartConfig(normalizeAsRepairQtyChartConfig(next))}
                                initialListExportConfig={asRepairListExportConfig}
                                onListExportConfigSaved={(next) => setAsRepairListExportConfig(normalizeAsRepairListExportConfig(next))}
                                showAppToast={showAppToast}
                            />
                        )}

                        {activePage === 'asRepairAdd' && canManageAsRepair && (
                            <AsRepairAddPage
                                canManageAsRepair={canManageAsRepair}
                                currentUserName={currentUser?.name || ''}
                                asRepairFormConfig={asRepairFormConfig}
                                managementScope={AS_REPAIR_MANAGEMENT_SCOPE.REPAIR}
                                pageTitle="A/S 목록 추가"
                                showAppToast={showAppToast}
                                onNavigateBack={() => goToPage('asRepair')}
                            />
                        )}

                        {activePage === 'asRepairPersonalAdd' && canManageAsRepair && (
                            <AsRepairAddPage
                                canManageAsRepair={canManageAsRepair}
                                currentUserName={currentUser?.name || ''}
                                asRepairFormConfig={asRepairPersonalFormConfig}
                                managementScope={AS_REPAIR_MANAGEMENT_SCOPE.PERSONAL}
                                pageTitle="A/S 목록 추가"
                                showAppToast={showAppToast}
                                onNavigateBack={() => goToPage('asRepairPersonal')}
                            />
                        )}

                        {activePage === 'settings' && (
                            <div className="placeholder-page settings-page">
                                <h3>설정</h3>
                                <div className="settings-row">
                                    <label className="settings-label" htmlFor="dark-mode-toggle">다크 모드</label>
                                    <AppleSwitchCheckbox
                                        id="dark-mode-toggle"
                                        checked={darkMode}
                                        onChange={(e) => toggleDarkMode(e.target.checked)}
                                    />
                                </div>
                                <div className="settings-row">
                                    <label className="settings-label">웹푸시 알림</label>
                                    <button
                                        className="button"
                                        onClick={() => ensurePushSubscription(String(currentUser?.name || '').trim())}
                                        disabled={pushRegistering}
                                    >
                                        {pushRegistering ? '등록 중...' : '알림 허용/재등록'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {activePage === 'permissions' && canManagePermissions && (
                            <div className="placeholder-page permission-page">
                                <h3>권한 관리</h3>
                                {permissionError && <div className="error-box">{permissionError}</div>}
                                {isPermissionLoading ? (
                                    <p>불러오는 중...</p>
                                ) : (
                                    <>
                                        <div className="top-buttons" style={{ width: '100%', margin: '0 0 12px 0' }}>
                                            <button className="button" onClick={handleSavePermissions} disabled={isSavingPermissions}>
                                                {isSavingPermissions ? '저장중...' : '저장'}
                                            </button>
                                        </div>
                                        <div className="permission-table-wrap">
                                            <table className="permission-table">
                                                <thead>
                                                    <tr>
                                                        <th>이름</th>
                                                        <th>전화번호</th>
                                                        <th>권한 관리</th>
                                                        <th>검토 권한</th>
                                                        <th>로그 관리</th>
                                                        <th>리스트 수정</th>
                                                        <th>세부사항 열람</th>
                                                        <th>펌웨어 다운로드</th>
                                                        <th>결선도 다운로드</th>
                                                        <th>모델별 사양 관리</th>
                                                        <th>A/S 수리 관리</th>
                                                        <th>사용자 삭제</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {permissionUsers.map((user) => (
                                                        <tr
                                                            key={user.id}
                                                            className={pendingDeleteUserIds.includes(user.id) ? 'permission-row-pending-delete' : ''}
                                                        >
                                                            <td>{user.name}</td>
                                                            <td>{user.phoneNumber || '-'}</td>
                                                            <td>
                                                                <AppleSwitchCheckbox
                                                                    checked={!!user.permissions?.canManagePermissions}
                                                                    disabled={pendingDeleteUserIds.includes(user.id)}
                                                                    onChange={(e) => updateUserPermission(user.id, 'canManagePermissions', e.target.checked)}
                                                                    aria-label={`${user.name} — 권한 관리`}
                                                                />
                                                            </td>
                                                            <td>
                                                                <AppleSwitchCheckbox
                                                                    checked={!!user.permissions?.canReviewList}
                                                                    disabled={pendingDeleteUserIds.includes(user.id)}
                                                                    onChange={(e) => updateUserPermission(user.id, 'canReviewList', e.target.checked)}
                                                                    aria-label={`${user.name} — 검토 권한`}
                                                                />
                                                            </td>
                                                            <td>
                                                                <AppleSwitchCheckbox
                                                                    checked={!!user.permissions?.canViewLogs}
                                                                    disabled={pendingDeleteUserIds.includes(user.id)}
                                                                    onChange={(e) => updateUserPermission(user.id, 'canViewLogs', e.target.checked)}
                                                                    aria-label={`${user.name} — 로그 관리`}
                                                                />
                                                            </td>
                                                            <td>
                                                                <AppleSwitchCheckbox
                                                                    checked={!!user.permissions?.canEditProduct}
                                                                    disabled={pendingDeleteUserIds.includes(user.id)}
                                                                    onChange={(e) => updateUserPermission(user.id, 'canEditProduct', e.target.checked)}
                                                                    aria-label={`${user.name} — 리스트 수정`}
                                                                />
                                                            </td>
                                                            <td>
                                                                <AppleSwitchCheckbox
                                                                    checked={!!user.permissions?.canViewDetail}
                                                                    disabled={pendingDeleteUserIds.includes(user.id)}
                                                                    onChange={(e) => updateUserPermission(user.id, 'canViewDetail', e.target.checked)}
                                                                    aria-label={`${user.name} — 세부사항 열람`}
                                                                />
                                                            </td>
                                                            <td>
                                                                <AppleSwitchCheckbox
                                                                    checked={!!user.permissions?.canDownloadFirmware}
                                                                    disabled={pendingDeleteUserIds.includes(user.id)}
                                                                    onChange={(e) => updateUserPermission(user.id, 'canDownloadFirmware', e.target.checked)}
                                                                    aria-label={`${user.name} — 펌웨어 다운로드`}
                                                                />
                                                            </td>
                                                            <td>
                                                                <AppleSwitchCheckbox
                                                                    checked={!!user.permissions?.canDownloadWiringDiagram}
                                                                    disabled={pendingDeleteUserIds.includes(user.id)}
                                                                    onChange={(e) => updateUserPermission(user.id, 'canDownloadWiringDiagram', e.target.checked)}
                                                                    aria-label={`${user.name} — 결선도 다운로드`}
                                                                />
                                                            </td>
                                                            <td>
                                                                <AppleSwitchCheckbox
                                                                    checked={!!user.permissions?.canManageModelSpecList}
                                                                    disabled={pendingDeleteUserIds.includes(user.id)}
                                                                    onChange={(e) => updateUserPermission(user.id, 'canManageModelSpecList', e.target.checked)}
                                                                    aria-label={`${user.name} — 모델별 사양 관리`}
                                                                />
                                                            </td>
                                                            <td>
                                                                <AppleSwitchCheckbox
                                                                    checked={!!user.permissions?.canManageAsRepair}
                                                                    disabled={pendingDeleteUserIds.includes(user.id)}
                                                                    onChange={(e) => updateUserPermission(user.id, 'canManageAsRepair', e.target.checked)}
                                                                    aria-label={`${user.name} — A/S 수리 관리`}
                                                                />
                                                            </td>
                                                            <td>
                                                                {pendingDeleteUserIds.includes(user.id) ? (
                                                                    <button
                                                                        type="button"
                                                                        className="button button-cancel permission-delete-cancel"
                                                                        onClick={() => cancelPendingDeleteUser(user.id)}
                                                                    >
                                                                        삭제 취소
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="button button-delete-spec permission-user-delete"
                                                                        onClick={() => requestMarkUserForDelete(user.id)}
                                                                        disabled={user.id === currentUser?.id}
                                                                    >
                                                                        사용자 삭제
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {activePage === 'logs' && canViewLogs && (() => {
                            const filteredLogs = logs.filter((log) => {
                                const timeStr = formatKoreanDateTime(log.createdAt);
                                const nameStr = formatLogUser(log);
                                const targetStr = formatLogTarget(log);
                                const descStr = formatLogDescription(log);
                                const f = logFilters;
                                return (
                                    (!f.time || timeStr.includes(f.time)) &&
                                    (!f.name || nameStr.toLowerCase().includes(f.name.toLowerCase())) &&
                                    (!f.target || targetStr.toLowerCase().includes(f.target.toLowerCase())) &&
                                    (!f.description || descStr.toLowerCase().includes(f.description.toLowerCase()))
                                );
                            });
                            const logTotalPages = Math.max(1, Math.ceil(filteredLogs.length / LOG_PAGE_SIZE));
                            const safeLogPage = Math.min(logPage, logTotalPages);
                            const pagedLogs = filteredLogs.slice((safeLogPage - 1) * LOG_PAGE_SIZE, safeLogPage * LOG_PAGE_SIZE);
                            return (
                                <div className="placeholder-page permission-page">
                                    <h3>로그 관리</h3>
                                    {logError && <div className="error-box">{logError}</div>}
                                    {isLogLoading ? (
                                        <p>불러오는 중...</p>
                                    ) : (
                                        <>
                                            <div className="permission-table-wrap">
                                                <table className="permission-table">
                                                    <thead>
                                                        <tr>
                                                            <th>시간</th>
                                                            <th>이름</th>
                                                            <th>대상</th>
                                                            <th>설명</th>
                                                        </tr>
                                                        <tr className="log-filter-row">
                                                            <th><input className="log-filter-input" type="text" placeholder="시간 필터" value={logFilters.time} onChange={(e) => { setLogFilters((p) => ({ ...p, time: e.target.value })); setLogPage(1); }} /></th>
                                                            <th><input className="log-filter-input" type="text" placeholder="이름 필터" value={logFilters.name} onChange={(e) => { setLogFilters((p) => ({ ...p, name: e.target.value })); setLogPage(1); }} /></th>
                                                            <th><input className="log-filter-input" type="text" placeholder="대상 필터" value={logFilters.target} onChange={(e) => { setLogFilters((p) => ({ ...p, target: e.target.value })); setLogPage(1); }} /></th>
                                                            <th><input className="log-filter-input" type="text" placeholder="설명 필터" value={logFilters.description} onChange={(e) => { setLogFilters((p) => ({ ...p, description: e.target.value })); setLogPage(1); }} /></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {pagedLogs.map((log) => (
                                                            <tr key={log.id}>
                                                                <td>{formatKoreanDateTime(log.createdAt)}</td>
                                                                <td>{formatLogUser(log)}</td>
                                                                <td>{formatLogTarget(log)}</td>
                                                                <td title={formatLogDescriptionTooltip(log)}>{formatLogDescription(log)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {logTotalPages > 1 && (
                                                <div className="pagination-wrap">
                                                    <button className="pagination-button" onClick={() => setLogPage((p) => Math.max(1, p - 1))} disabled={safeLogPage === 1}>이전</button>
                                                    {Array.from({ length: logTotalPages }, (_, i) => i + 1).map((page) => (
                                                        <button key={page} className={`pagination-button ${page === safeLogPage ? 'active' : ''}`} onClick={() => setLogPage(page)}>{page}</button>
                                                    ))}
                                                    <button className="pagination-button" onClick={() => setLogPage((p) => Math.min(logTotalPages, p + 1))} disabled={safeLogPage === logTotalPages}>다음</button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })()}
                    </section>
                </div>
            )}

            <div className="container">
                {activeFormType && (
                    <AddSpecPopup 
                        onAddModel={(model) => handleAddModel(model, activeFormType)} 
                        onClose={closeAddPage} 
                        activeType={activeFormType}
                        onSwitchType={openAddPage}
                        type={activeFormType === 'adjuster' ? '조절기' : '제어기'}
                        specDropdownOptions={specDropdownOptions}
                        productRegistrationConfig={productRegistrationConfig}
                        allowAddDropdownOption={canManageSpecDropdowns}
                        onAddDropdownOption={handleAddSpecDropdownOption}
                        showAppToast={showAppToast}
                        hideAppToast={hideAppToast}
                        hideLocationAndSiteFields={addFormListScope === PRODUCT_LIST_SCOPE_MODEL_SPEC}
                    />
                )}
            </div>
            {showBulkFirmwarePendingPopup && (
                <div className="modal-backdrop" onClick={() => setShowBulkFirmwarePendingPopup(false)}>
                    <div className="modal-content bulk-pending-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>펌웨어 일괄 업로드 리스트</h3>
                            <button className="modal-close" onClick={() => setShowBulkFirmwarePendingPopup(false)}>×</button>
                        </div>
                        {bulkFirmwarePendingRows.length === 0 ? (
                            <p className="bulk-pending-empty">펌웨어 일괄 업로드로 올라온 승인 대기 리스트가 없습니다.</p>
                        ) : (
                            <>
                                <div className="bulk-pending-list">
                                    {bulkFirmwarePendingGroups.map((group) => (
                                        <div key={group.key} className="bulk-pending-group">
                                            <div className="bulk-pending-group-head">
                                                <div className="bulk-pending-group-meta">
                                                    <span className="bulk-pending-group-label">펌웨어 파일명</span>
                                                    <span className="bulk-pending-group-value">{group.fileName}</span>
                                                </div>
                                                <div className="bulk-pending-group-meta">
                                                    <span className="bulk-pending-group-label">메모</span>
                                                    <span className="bulk-pending-group-value">{group.memo}</span>
                                                </div>
                                            </div>
                                            <div className="bulk-pending-group-body">
                                                {group.items.map((item) => (
                                                    <label key={item.id} className="bulk-pending-item">
                                                        <AppleSwitchCheckbox
                                                            checked={bulkPendingSelectedIds.includes(item.id)}
                                                            onChange={(e) => handleToggleBulkPendingSelect(item.id, e.target.checked)}
                                                            disabled={isBulkPendingApproving || isBulkPendingRejecting}
                                                            aria-label={`선택: ${item.siteName} / ${item.name}`}
                                                        />
                                                        <span className="bulk-pending-text">
                                                            {item.siteName} / {item.name} / 요청자: {item.pendingRequesterName || '-'}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <textarea
                                    className="field-input bulk-pending-reject-input"
                                    placeholder="승인 거부 내용을 입력해주세요."
                                    value={bulkPendingRejectReason}
                                    onChange={(e) => setBulkPendingRejectReason(e.target.value)}
                                    disabled={isBulkPendingApproving || isBulkPendingRejecting}
                                />
                            </>
                        )}
                        <div className="modal-actions">
                            <button
                                className="button"
                                onClick={handleApproveBulkFirmwarePending}
                                disabled={isBulkPendingApproving || isBulkPendingRejecting || bulkPendingSelectedIds.length === 0}
                            >
                                {isBulkPendingApproving ? '처리중...' : '최종 승인'}
                            </button>
                            <button
                                className="button button-cancel"
                                onClick={handleRejectBulkFirmwarePending}
                                disabled={isBulkPendingApproving || isBulkPendingRejecting || bulkPendingSelectedIds.length === 0}
                            >
                                {isBulkPendingRejecting ? '처리중...' : '승인 거부'}
                            </button>
                            <button
                                className="button button-cancel"
                                onClick={() => setShowBulkFirmwarePendingPopup(false)}
                                disabled={isBulkPendingApproving || isBulkPendingRejecting}
                            >
                                취소
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {rejectTargetId && (
                <div className="modal-backdrop" onClick={closeRejectModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>승인 거부</h3>
                            <button className="modal-close" onClick={closeRejectModal}>×</button>
                        </div>
                        <textarea
                            className="field-input reject-reason-input"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="승인 거부 내용을 입력해주세요."
                        />
                        <div className="modal-actions">
                            <button className="button" onClick={handleRejectProduct} disabled={isRejecting || !rejectReason.trim()}>
                                {isRejecting ? '처리중...' : '승인 거부 확정'}
                            </button>
                            <button className="button button-cancel" onClick={closeRejectModal} disabled={isRejecting}>취소</button>
                        </div>
                    </div>
                </div>
            )}
            {appToastMessage && (
                <div className={`toast-popup ${appToastVisible ? 'show' : ''} toast-${appToastType === 'error' ? 'error' : appToastType === 'info' ? 'info' : 'success'}`}>
                    {appToastMessage}
                </div>
            )}
        </div>
    );
}

export default App;